import { getPublicMediaRoot } from '../../serviceAgent';
import Documents from '../../Documents';
import { getUsersDisableContentTags, getUsersEnableContentTags } from '../../GlobalFunctions';
import { ClassroomType } from '@peekapak/types/DataProtocolTypes';

export type GradeRange = -1 | 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export interface ApplicationTags {
  applicationTags?: string[];
}

export interface LibraryCardData extends ApplicationTags {
  content: string;
  displayName: string;
  filterTags: string[];
  gradeSpan?: number[];
  subtitle?: string | string[];
  type: string;
}

export interface UnitCardData {
  gradeSpan: number[];
  unit: string;
  isTrialable?: boolean;
}

export interface ElementaryCardData {
  displayNameSpanish?: string;
  contentSpanish?: string;
  hasSpanishContent?: boolean;
  hasSpanishLibraryCardContent?: boolean;
  hasPeekavilleContent?: boolean;
  featuredImage: string;
}

export interface ElementaryLessonCardData {
  label: string;
  index?: number;
  buttonText?: string;
}

export interface MiddleSchoolCardData {
  featuredImage: string;
  slug: string;
  numModules: number;
}

export interface VideoCardData {
  featuredImage: string;
  assetId: string;
  isTrialable?: boolean;
  unit?: string;
}

export interface LinkCardData {
  featuredImage: string;
  slug: string;
  buttonText: string;
  unit?: string;
}

export interface LibraryCardProps {
  displayName: string | undefined;
  isShow?: boolean;
  subtitle?: string | string[];
  content: string | undefined;
  featuredImage?: string;
}

export interface UnitCardProps {
  percentComplete: number;
  unit: string;
  isLocked: boolean;
  classroom: ClassroomType;
  isWrongGrade?: boolean;
  isShow?: boolean;
  hasPeekavilleContent?: boolean;
  isPeekavilleLocked?: boolean;
  gradeSpan?: number[];
}

export interface ElementaryLessonCardProps {
  unit: string;
  buttonText?: string;
  classroom: ClassroomType;
  label: string;
  index?: number;
  level?: string;
}

export interface SecondaryLessonCardProps {
  lessonIndex?: number;
  moduleIndex?: number;
  lessonType?: string;
  slug?: string;
}

export interface VideoCardProps {
  assetId: string;
  featuredImage: string;
  isLocked?: boolean;
  isWrongGrade: boolean;
  gradeSpan?: number[];
}

export interface LinkCardProps {
  buttonText: string;
  slug: string;
  isLocked?: boolean;
  classroom: ClassroomType;
  isWrongGrade: boolean;
  isUpgradable?: boolean;
}

export interface AllCardData {
  displayName: string;
  displayNameSpanish?: string;
  subtitle?: string | string[];
  content: string;
  contentSpanish?: string;
  filterTags: string[];
  type: string;
  gradeSpan?: number[];
  unit?: string;
  isTrialable?: boolean;
  hasSpanishContent?: boolean;
  hasPeekavilleContent?: boolean;
  featuredImage?: string;
  slug?: string;
  numModules?: number;
  assetId?: string;
  buttonText?: string;
  isWrongGrade?: boolean;
  isLocked?: boolean;
  label?: string;
  index?: number;
  order?: number;
  lessonIndex?: number;
  moduleIndex?: number;
  lessonType?: string;
}

export type ElementaryCard = LibraryCardData & UnitCardData & ElementaryCardData;
export type MiddleSchoolCard = LibraryCardData & UnitCardData & MiddleSchoolCardData;
export type VideoCard = LibraryCardData & VideoCardData;
export type LinkCard = LibraryCardData & LinkCardData;
export type ElementaryLessonCard = LibraryCardData & UnitCardData & ElementaryLessonCardData;

export const elementaryUnits: ElementaryCard[] = [
  {
    displayName: 'Self-Regulation',
    displayNameSpanish: 'Autocontrol',
    unit: 'Self-Regulation',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    hasSpanishContent: true,
    content:
      'Self-regulation is a skill that helps students manage their behaviors and express emotions in constructive ways.',
    contentSpanish:
      'El autocontrol es una habilidad que ayuda a los estudiantes a manejar su comportamiento y expresar sus emociones de forma constructiva.',
    isTrialable: true,
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    type: 'elementary',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/Self-RegulationBookshelf.png`,
  },
  {
    displayName: 'Self-Regulation',
    displayNameSpanish: 'Autocontrol',
    unit: 'Self-RegulationMena',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    hasSpanishContent: true,
    content:
      'Self-regulation is a skill that helps students manage their behaviors and express emotions in constructive ways.',
    contentSpanish:
      'El autocontrol es una habilidad que ayuda a los estudiantes a manejar su comportamiento y expresar sus emociones de forma constructiva.',
    isTrialable: true,
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    type: 'elementary',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/Self-RegulationMenaBookshelf.png`,
  },
  {
    displayName: 'Respect',
    displayNameSpanish: 'Respeto',
    unit: 'Respect',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    hasSpanishContent: true,
    isTrialable: true,
    content:
      'Respect is a mindset that guides how we treat others. It teaches us to act in a manner that shows we care about the feelings and well-being of others, our environment, and ourselves.',
    contentSpanish:
      'El respeto es una mentalidad que guía cómo tratamos a los demás. Nos enseña a actuar de forma en que nos preocupamos por los sentimientos y bienestar de las demás personas, nuestro ambiente y nosotros mismos.',
    hasPeekavilleContent: true,
    applicationTags: ['standard', 'not-mena'],
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/RespectBookshelf.png`,
  },
  {
    displayName: 'Respect',
    displayNameSpanish: 'Respeto',
    unit: 'RespectMena',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    hasSpanishContent: true,
    isTrialable: true,
    content:
      'Respect is a mindset that guides how we treat others. It teaches us to act in a manner that shows we care about the feelings and well-being of others, our environment, and ourselves.',
    contentSpanish:
      'El respeto es una mentalidad que guía cómo tratamos a los demás. Nos enseña a actuar de forma en que nos preocupamos por los sentimientos y bienestar de las demás personas, nuestro ambiente y nosotros mismos.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/RespectMenaBookshelf.png`,
  },
  {
    displayName: 'Gratitude',
    displayNameSpanish: 'Gratitud',
    unit: 'Gratitude',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Gratitude is feeling thankful for what we have, recognizing our blessings (material or nonmaterial), and showing appreciation through acknowledging or returning a kind gesture.',
    contentSpanish:
      'La gratitud se refiere a estar agradecidos por lo que tenemos, reconociendo nuestras bendiciones (materiales o no materiales) y mostrando aprecio por medio del reconocimiento y/o haciendo algún tiempo de gesto a cambio.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['standard', 'not-mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/GratitudeBookshelf.png`,
  },
  {
    displayName: 'Gratitude',
    displayNameSpanish: 'Gratitud',
    unit: 'GratitudeMena',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Gratitude is feeling thankful for what we have, recognizing our blessings (material or nonmaterial), and showing appreciation through acknowledging or returning a kind gesture.',
    contentSpanish:
      'La gratitud se refiere a estar agradecidos por lo que tenemos, reconociendo nuestras bendiciones (materiales o no materiales) y mostrando aprecio por medio del reconocimiento y/o haciendo algún tiempo de gesto a cambio.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/GratitudeMenaBookshelf.png`,
  },
  {
    displayName: 'Perseverance',
    displayNameSpanish: 'Perseverancia',
    unit: 'Perseverance',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Perseverance is being steadily persistent and willing to continue on with a task or goal despite challenges or a delay in achieving success.',
    contentSpanish:
      'Perseverancia significa persistir continuamente y estar dispuesto a continuar con alguna labor o meta aún cuando hay desafíos o demoras para llegar al éxito.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['standard', 'not-mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/PerseveranceBookshelf.png`,
  },
  {
    displayName: 'Perseverance',
    unit: 'PerseveranceMena',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Perseverance is being steadily persistent and willing to continue on with a task or goal despite challenges or a delay in achieving success.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/PerseveranceMenaBookshelf.png`,
  },
  {
    displayName: 'Empathy',
    displayNameSpanish: 'Empatía',
    unit: 'Empathy',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Empathy is understanding and sensing others; emotions and imagining what they are thinking and how they are feeling.',
    contentSpanish:
      'La empatía es comprender y percibir las emociones de los demás, así como imaginar qué están pensando y cómo se sienten.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['standard', 'not-mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/EmpathyBookshelf.png`,
  },
  {
    displayName: 'Empathy',
    displayNameSpanish: 'Empatía',
    unit: 'EmpathyMena',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Empathy is understanding and sensing others; emotions and imagining what they are thinking and how they are feeling.',
    contentSpanish:
      'La empatía es comprender y percibir las emociones de los demás, así como imaginar qué están pensando y cómo se sienten.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/EmpathyMenaBookshelf.png`,
  },
  {
    displayName: 'Teamwork',
    displayNameSpanish: 'Trabajo en equipo',
    unit: 'Teamwork',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content: 'Teamwork means working cooperatively with others to achieve a shared goal.',
    contentSpanish:
      'El trabajo en equipo significa trabajar cooperando con los demás para alcanzar una meta compartida.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['standard', 'not-mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/TeamworkBookshelf.png`,
  },
  {
    displayName: 'Teamwork',
    displayNameSpanish: 'Trabajo en equipo',
    unit: 'TeamworkMena',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content: 'Teamwork means working cooperatively with others to achieve a shared goal.',
    contentSpanish:
      'El trabajo en equipo significa trabajar cooperando con los demás para alcanzar una meta compartida.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/TeamworkMenaBookshelf.png`,
  },
  {
    displayName: 'Kindness',
    displayNameSpanish: 'Amabilidad',
    unit: 'Kindness',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Kindness is being considerate, caring, and respectful of others and acting in ways that will benefit others without expecting anything in return.',
    contentSpanish:
      'La amabilidad significa ser considerados, cariñosos y respetuosos con los demás, así como actuar de formas que beneficiarán a otros sin esperar nada a cambio.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['standard', 'not-mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/KindnessBookshelf.png`,
  },
  {
    displayName: 'Kindness',
    displayNameSpanish: 'Amabilidad',
    unit: 'KindnessMena',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Kindness is being considerate, caring, and respectful of others and acting in ways that will benefit others without expecting anything in return.',
    contentSpanish:
      'La amabilidad significa ser considerados, cariñosos y respetuosos con los demás, así como actuar de formas que beneficiarán a otros sin esperar nada a cambio.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/KindnessMenaBookshelf.png`,
  },
  {
    displayName: 'Honesty',
    displayNameSpanish: 'Honestidad',
    unit: 'Honesty',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Honesty is being truthful in what we say and do. Honesty can be demonstrated through what we say, such as telling the truth, including only facts, and admitting when we are wrong.',
    contentSpanish:
      'La honestidad es ser sinceros en lo que decimos y hacemos. Se puede demostrar por medio de lo que decimos, como decir la verdad, incluyendo solamente hechos y admitiendo cuando nos equivocamos.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['standard', 'not-mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/HonestyBookshelf.png`,
  },
  {
    displayName: 'Honesty',
    displayNameSpanish: 'Honestidad',
    unit: 'HonestyMena',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Honesty is being truthful in what we say and do. Honesty can be demonstrated through what we say, such as telling the truth, including only facts, and admitting when we are wrong.',
    contentSpanish:
      'La honestidad es ser sinceros en lo que decimos y hacemos. Se puede demostrar por medio de lo que decimos, como decir la verdad, incluyendo solamente hechos y admitiendo cuando nos equivocamos.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/HonestyMenaBookshelf.png`,
  },
  {
    displayName: 'Optimism',
    displayNameSpanish: 'Optimismo',
    unit: 'Optimism',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Optimism is a form of positive thinking that guides us in our belief about how successful we think our actions and outcomes will be.',
    contentSpanish:
      'El optimismo es una forma de pensamiento positivo que nos guía en nuestras creencias sobre cuán exitosos creemos que seremos en nuestras acciones y resultados.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['standard', 'not-mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/OptimismBookshelf.png`,
  },
  {
    displayName: 'Optimism',
    displayNameSpanish: 'Optimismo',
    unit: 'OptimismMena',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Optimism is a form of positive thinking that guides us in our belief about how successful we think our actions and outcomes will be.',
    contentSpanish:
      'El optimismo es una forma de pensamiento positivo que nos guía en nuestras creencias sobre cuán exitosos creemos que seremos en nuestras acciones y resultados.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/OptimismMenaBookshelf.png`,
  },
  {
    displayName: 'Courage',
    displayNameSpanish: 'Valentía',
    unit: 'Courage',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      "Courage is being brave, especially when faced with difficult situations and unfamiliar experiences. Courage is acting in accordance with one's beliefs, even if it's against what others may think.",
    contentSpanish:
      'La valentía se refiere a ser calientes, especialmente cuando debemos afrontar situaciones difíciles y experiencias desconocidas. También se puede referir a actuar de acuerdo a nuestras creencias, aún cuando vamos en contra de lo que otros pueden pensar.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['standard', 'not-mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/CourageBookshelf.png`,
  },
  {
    displayName: 'Courage',
    displayNameSpanish: 'Valentía',
    unit: 'CourageMena',
    hasSpanishContent: true,
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      "Courage is being brave, especially when faced with difficult situations and unfamiliar experiences. Courage is acting in accordance with one's beliefs, even if it's against what others may think.",
    contentSpanish:
      'La valentía se refiere a ser calientes, especialmente cuando debemos afrontar situaciones difíciles y experiencias desconocidas. También se puede referir a actuar de acuerdo a nuestras creencias, aún cuando vamos en contra de lo que otros pueden pensar.',
    hasPeekavilleContent: true,
    filterTags: ['elementary', 'freemium'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/CourageMenaBookshelf.png`,
  },
  {
    unit: 'Pandemic-SEL',
    displayNameSpanish: 'Manejando nuestros sentimientos en una pandemia',
    displayName: 'Pandemic SEL',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    hasSpanishContent: true,
    content:
      'This unit welcomes students back to school under COVID-19 conditions. This unit will help classrooms establish and practice new routines, notice and name their concerns, and deal with changes that occur throughout the beginning of the year.',
    contentSpanish:
      'Esta unidad le da la bienvenida a los estudiantes durante la pandemia de COVID-19. Esta unidad ayudará a los grupos estudiantiles a establecer y practicar nuevas rutinas, poder notar y nombrar sus preocupaciones y tratar con los cambios que toman lugar a principio del año.',
    filterTags: ['supplementary'],
    type: 'elementary',
    applicationTags: ['not-mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/Pandemic-SELBookshelf.png`,
  },
  {
    displayName: 'Friendship',
    unit: 'Friendship',
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Friendship is shared between two or more people who trust and care about each other and enjoy spending time together.',
    filterTags: ['supplementary'],
    type: 'elementary',
    applicationTags: ['standard', 'not-mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/FriendshipBookshelf.png`,
  },
  {
    displayName: 'Imagination',
    unit: 'Imagination',
    isTrialable: true,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content: 'Imagination is the ability to be creative with your mind to picture things that aren’t there.',
    filterTags: ['supplementary'],
    type: 'elementary',
    applicationTags: ['standard', 'not-mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/ImaginationBookshelf.png`,
  },
  {
    displayName: 'Problem-Solving',
    unit: 'Problem-Solving',
    gradeSpan: [-1, 0, 1, 2],
    content: 'Problem-Solving is being able to identify solutions to an issue despite difficulty or challenges.',
    filterTags: ['supplementary'],
    type: 'elementary',
    applicationTags: ['standard', 'not-mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/Problem-SolvingBookshelf.png`,
  },
  {
    displayName: 'Math Anxiety',
    unit: 'Math-Anxiety',
    gradeSpan: [3, 4, 5],
    content: 'The stressful feeling that many of us get when doing math is called “math anxiety”.',
    filterTags: ['supplementary'],
    type: 'elementary',
    applicationTags: ['standard', 'not-mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/Math-AnxietyBookshelf.png`,
  },
  {
    displayName: 'Celebrating Differences',
    unit: 'Celebrating-Differences',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content: 'In this unit, students will learn to be curious about our differences and celebrate them.',
    filterTags: ['supplementary'],
    type: 'elementary',
    applicationTags: ['x-race'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/Celebrating-DifferencesBookshelf.png`,
  },
  {
    displayName: 'Arabic Calligraphy',
    unit: 'Arabic-Calligraphy',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Students explore Arabic Calligraphy as a mindful art that connects mind and body, fostering self-regulation through focus, breathwork, and creative expression.',
    filterTags: ['supplementary'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/CalligraphyK5.png`,
  },
  {
    displayName: 'Gratitude & Ramadan',
    unit: 'Gratitude-Ramadan',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Students explore gratitude through Ramadan traditions that emphasize reflection, appreciation, and giving.',
    filterTags: ['supplementary'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/Elem_UnitLibrary-00_Grat_Ramadan.png`,
  },
  {
    displayName: 'Kindness & Dates',
    unit: 'Kindness-Dates',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Students explore the cultural tradition of offering dates as a kind and traditional UAE gesture, guiding students to build relationships, foster community, and create personal and classroom practices of generosity. ',
    filterTags: ['supplementary'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/Elem_UnitLibrary - 00_Kind Dates.png`,
  },
  {
    displayName: 'Empathy & Emirati Poetry',
    unit: 'Empathy-Poetry',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Students explore empathy through the rich oral and written Emirati traditions of poetry, where poets express deep emotion and personal stories.  ',
    filterTags: ['supplementary'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/Elem_UnitLibrary - 00_Poetry.png`,
  },
  {
    displayName: 'Teamwork & The Ministry of Tolerance ',
    unit: 'Teamwork-Tolerance',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    content:
      'Guided by the UAE Ministry of Tolerance values, this unit explores the skill of teamwork through the context of diversity and collaboration within the United Arab Emirates. ',
    filterTags: ['supplementary'],
    type: 'elementary',
    applicationTags: ['mena'],
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/Elem_UnitLibrary - 00_Tolerance.png`,
  },
];

// Takween-specific units (MENA subset with exclusive access)
// Middle School Takween Units (Grades 6-8)
// Unified Takween Units (Grades 6-12) - single unit definition works for all secondary grades
export const takweenUnits: MiddleSchoolCard[] = [
  {
    unit: 'building-resilience-and-wellbeing-mena',
    numModules: 8,
    slug: '/lessonPlan/building-resilience-and-wellbeing-mena',
    displayName: 'بناء المرونة والرفاهية',
    subtitle: 'placeholder subtitle',
    content: 'placeholder',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/ar-takween-unit-cover-page-01.png`,
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    filterTags: ['takween', 'middleSchool', 'highSchool'],
    type: 'middleSchool', // Keep as middleSchool for component compatibility
    applicationTags: ['mena', 'takween-exclusive'],
  },
];

export const elementaryFreemiumLessons: ElementaryLessonCard[] = [
  {
    content: 'Students are introduced to the unit through a letter from Leo.',
    displayName: 'Letter from Leo!',
    filterTags: ['elementary', 'freemium'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    index: 1,
    label: 'Letter',
    subtitle: 'Self Regulation: Lesson 1',
    type: 'lesson',
    unit: 'Self-Regulation',
  },
  {
    content: 'Students are introduced to the unit through a letter from Zoey.',
    displayName: 'Letter from Zoey!',
    filterTags: ['elementary', 'freemium'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    index: 1,
    label: 'Letter',
    subtitle: 'Respect: Lesson 1',
    type: 'lesson',
    unit: 'Respect',
  },
  {
    content: 'Students are introduced to the unit through a letter from Cody.',
    displayName: 'Letter from Cody!',
    filterTags: ['elementary', 'freemium'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    index: 1,
    label: 'Letter',
    subtitle: 'Gratitude: Lesson 1',
    type: 'lesson',
    unit: 'Gratitude',
  },
  {
    content: 'Students are introduced to the unit through a letter from Brady.',
    displayName: 'Letter from Brady!',
    filterTags: ['elementary', 'freemium'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    index: 1,
    label: 'Letter',
    subtitle: 'Perseverance: Lesson 1',
    type: 'lesson',
    unit: 'Perseverance',
  },
  {
    content: 'Students are introduced to the unit through a letter from Inés.',
    displayName: 'Letter from Inés!',
    filterTags: ['elementary', 'freemium'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    index: 1,
    label: 'Letter',
    subtitle: 'Empathy: Lesson 1',
    type: 'lesson',
    unit: 'Empathy',
  },
  {
    content: 'Students are introduced to the unit through a letter from Sebastian.',
    displayName: 'Letter from Sebastian!',
    filterTags: ['elementary', 'freemium'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    index: 1,
    label: 'Letter',
    subtitle: 'Teamwork: Lesson 1',
    type: 'lesson',
    unit: 'Teamwork',
  },
  {
    content: 'Students are introduced to the unit through a letter from Kenji.',
    displayName: 'Letter from Kenji!',
    filterTags: ['elementary', 'freemium'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    index: 1,
    label: 'Letter',
    subtitle: 'Kindness: Lesson 1',
    type: 'lesson',
    unit: 'Kindness',
  },
  {
    content: 'Students are introduced to the unit through a letter from Menka.',
    displayName: 'Letter from Menka!',
    filterTags: ['elementary', 'freemium'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    index: 1,
    label: 'Letter',
    subtitle: 'Honesty: Lesson 1',
    type: 'lesson',
    unit: 'Honesty',
  },
  {
    content: 'Students are introduced to the unit through a letter from Lucia.',
    displayName: 'Letter from Lucia!',
    filterTags: ['elementary', 'freemium'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    index: 1,
    label: 'Letter',
    subtitle: 'Optimism: Lesson 1',
    type: 'lesson',
    unit: 'Optimism',
  },
  {
    content: 'Students are introduced to the unit through a letter from Apollo.',
    displayName: 'Letter from Apollo!',
    filterTags: ['elementary', 'freemium'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    index: 1,
    label: 'Letter',
    subtitle: 'Courage: Lesson 1',
    type: 'lesson',
    unit: 'Courage',
  },
  {
    content: 'Students read “The Peekapak Pals and the Classroom Chaos”',
    displayName: 'Classroom Chaos Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Self Regulation Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Self-Regulation',
  },
  {
    content: 'Students read “The Peekapak Pals and the Classroom Chaos”',
    displayName: 'Classroom Chaos Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Self Regulation Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Self-RegulationMena',
  },
  {
    content: 'Students read “The Peekapak Pals and the Berryball Champ.”',
    displayName: 'Berryball Champ Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Respect Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Respect',
  },
  {
    content: 'Students read “The Peekapak Pals and the Berryball Champ.”',
    displayName: 'Berryball Champ Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Respect Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'RespectMena',
  },
  {
    content: 'Students read “The Peekapak Pals and the Tree of Gratitude.”',
    displayName: 'Tree of Gratitude Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Gratitude Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Gratitude',
  },
  {
    content: 'Students read “The Peekapak Pals and the Tree of Gratitude.”',
    displayName: 'Tree of Gratitude Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Gratitude Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'GratitudeMena',
  },
  {
    content: 'Students read “The Peekapak Pals and the Sounds of Music.”',
    displayName: 'The Sounds of Music Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Perseverance Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Perseverance',
  },
  {
    content: 'Students read “The Peekapak Pals and the Sounds of Music.”',
    displayName: 'The Sounds of Music Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Perseverance Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'PerseveranceMena',
  },
  {
    content: 'Students read “The Peekapak Pals and the New Student.”',
    displayName: 'The New Student Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Empathy Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Empathy',
  },
  {
    content: 'Students read “The Peekapak Pals and the New Student.”',
    displayName: 'The New Student Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Empathy Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'EmpathyMena',
  },
  {
    content: 'Students read “The Peekapak Pals and the Robot Challenge.”',
    displayName: 'Robot Challenge Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Teamwork Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Teamwork',
  },
  {
    content: 'Students read “The Peekapak Pals and the Robot Challenge.”',
    displayName: 'Robot Challenge Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Teamwork Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'TeamworkMena',
  },
  {
    content: 'Students read “The Peekapak Pals and the Season of Kindness.”',
    displayName: 'Season of Kindness Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Kindness Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Kindness',
  },
  {
    content: 'Students read “The Peekapak Pals and the Season of Kindness.”',
    displayName: 'Season of Kindness Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Kindness Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'KindnessMena',
  },
  {
    content: 'Students read “The Peekapak Pals and the Trouble with the Truth.”',
    displayName: 'Trouble with the Truth Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Honesty Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Honesty',
  },
  {
    content: 'Students read “The Peekapak Pals and the Trouble with the Truth.”',
    displayName: 'Trouble with the Truth Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Honesty Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'HonestyMena',
  },
  {
    content: 'Students read “The Peekapak Pals and the Mysterious Box.”',
    displayName: 'Mysterious Box Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Optimism Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Optimism',
  },
  {
    content: 'Students read “The Peekapak Pals and the Mysterious Box.”',
    displayName: 'Mysterious Box Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Optimism Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'OptimismMena',
  },
  {
    content: 'Students read “The Peekapak Pals and a Splash of Courage.”',
    displayName: 'A Splash of Courage Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Courage Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Courage',
  },
  {
    content: 'Students read “The Peekapak Pals and a Splash of Courage.”',
    displayName: 'A Splash of Courage Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Courage Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'CourageMena',
  },
  {
    content: 'Students read “Test”',
    displayName: 'Calligraphy Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Calligraphy Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Arabic-Calligraphy',
  },
  {
    content: 'Students read “Test”',
    displayName: 'Gratitude Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Gratitude Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Gratitude-Ramadan',
  },
  {
    content: 'Students read “Test”',
    displayName: 'Kindness Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Kindness Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Kindness-Dates',
  },
  {
    content: 'Students read “Test”',
    displayName: 'Empathy Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Empathy Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Empathy-Poetry',
  },
  {
    content: 'Students read “Test”',
    displayName: 'Teamwork Read Aloud',
    filterTags: ['elementary', 'freemium'],
    applicationTags: ['mena'],
    gradeSpan: [0, 1, 2, 3, 4, 5],
    label: 'Storybook',
    subtitle: 'Teamwork Story',
    buttonText: 'Read',
    type: 'lesson',
    unit: 'Teamwork-Tolerance',
  },
];

export const middleSchoolUnits: MiddleSchoolCard[] = [
  {
    unit: 'welcome',
    numModules: 7,
    slug: '/lessonPlan/welcome',
    displayName: 'Welcome to Peekapak',
    subtitle: 'Overview of CASEL',
    content:
      'Overview of CASEL and how to use Peekapak to support secondary students, a critical time for students to explore their identity, make decisions, regulate emotions, resolve conflicts and manage stress and anxiety.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U0-Welcome.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    type: 'middleSchool',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    unit: 'welcome-mena',
    numModules: 7,
    slug: '/lessonPlan/welcome-mena',
    displayName: 'Welcome to Peekapak',
    subtitle: 'Overview of CASEL',
    content:
      'Overview of CASEL and how to use Peekapak to support secondary students, a critical time for students to explore their identity, make decisions, regulate emotions, resolve conflicts and manage stress and anxiety.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U0-Welcome.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    type: 'middleSchool',
    applicationTags: ['mena'],
  },
  {
    unit: 'identity',
    numModules: 14,
    slug: '/lessonPlan/identity',
    displayName: 'Identity & Self-Awareness',
    subtitle: 'CASEL: Self-Awareness',
    content:
      'Identity is how we see ourselves and tell our life story. Identity formation or identity development is the process of understanding one’s self. Students reflect on the essential identity questions...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U1-Identity.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool'],
    type: 'middleSchool',
    applicationTags: ['x-lgbtq'],
  },
  {
    unit: 'identity-standard',
    numModules: 15,
    slug: '/lessonPlan/identity-standard',
    displayName: 'Self-Awareness',
    subtitle: 'CASEL: Self-Awareness',
    content:
      'Self-awareness is the foundation of personal growth and development. It involves being able to recognize and understand our thoughts, feelings, and behaviors, as well as how they affect ourselves and others...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U1-Identity.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    type: 'middleSchool',
    applicationTags: ['standard', 'not-x-lgbtq', 'not-mena'],
  },
  {
    unit: 'identity-mena',
    numModules: 15,
    slug: '/lessonPlan/identity-mena',
    displayName: 'Self-Awareness',
    subtitle: 'CASEL: Self-Awareness',
    content:
      'Self-awareness is the foundation of personal growth and development. It involves being able to recognize and understand our thoughts, feelings, and behaviors, as well as how they affect ourselves and others...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U1-Identity.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool'],
    type: 'middleSchool',
    applicationTags: ['mena'],
  },
  {
    unit: 'stress',
    numModules: 12,
    slug: '/lessonPlan/stress',
    displayName: 'Stress and Regulation',
    subtitle: 'CASEL: Self-Management',
    content:
      'Emotional regulation is the ability to manage and express emotions in healthy, constructive ways. Emotional regulation builds upon self-awareness and involves the application of strategies...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U2-Stress.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    applicationTags: ['x-lgbtq'],
    isTrialable: true,
    type: 'middleSchool',
  },
  {
    unit: 'stress-standard',
    numModules: 15,
    slug: '/lessonPlan/stress-standard',
    displayName: 'Stress and Regulation',
    subtitle: 'CASEL: Self-Management',
    content:
      'Emotional regulation is the ability to manage and express emotions in healthy, constructive ways. Emotional regulation builds upon self-awareness and involves the application of strategies...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U2-Stress.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    type: 'middleSchool',
    applicationTags: ['standard', 'not-x-lgbtq', 'not-mena'],
  },
  {
    unit: 'stress-mena',
    numModules: 17,
    slug: '/lessonPlan/stress-mena',
    displayName: 'Stress and Regulation',
    subtitle: 'CASEL: Self-Management',
    content:
      'Emotional regulation is the ability to manage and express emotions in healthy, constructive ways. Emotional regulation builds upon self-awareness and involves the application of strategies...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U2-Stress.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    applicationTags: ['mena'],
    isTrialable: true,
    type: 'middleSchool',
  },
  {
    unit: 'decision',
    numModules: 15,
    slug: '/lessonPlan/decision',
    displayName: 'Decision Making Skills',
    subtitle: 'CASEL: Responsible Decision Making',
    content:
      'Decision making is the process of using best judgement to make a decision and problem solve. In this unit, students learn about the decision making process, how to think critically about a decision...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U3-DecisionMaking.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    applicationTags: ['x-lgbtq'],
    type: 'middleSchool',
  },
  {
    unit: 'decision-standard',
    numModules: 15,
    slug: '/lessonPlan/decision-standard',
    displayName: 'Decision Making Skills',
    subtitle: 'CASEL: Responsible Decision Making',
    content:
      'Decision making is the process of using best judgement to make a decision and problem solve. In this unit, students learn about the decision making process, how to think critically about a decision...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U3-DecisionMaking.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    type: 'middleSchool',
    applicationTags: ['standard', 'not-x-lgbtq', 'not-mena'],
  },
  {
    unit: 'decision-mena',
    numModules: 15,
    slug: '/lessonPlan/decision-mena',
    displayName: 'Decision Making Skills',
    subtitle: 'CASEL: Responsible Decision Making',
    content:
      'Decision making is the process of using best judgement to make a decision and problem solve. In this unit, students learn about the decision making process, how to think critically about a decision...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U3-DecisionMaking.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    applicationTags: ['mena'],
    type: 'middleSchool',
  },
  {
    unit: 'belonging',
    numModules: 15,
    slug: '/lessonPlan/belonging',
    displayName: 'Belonging & Peer Influence',
    subtitle: 'CASEL: Relationship Skills',
    content:
      'Relationship skills and conflict resolution focuses on students developing positive and healthy relationships with diverse individuals and a variety of groups...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U4-Belonging.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    applicationTags: ['x-lgbtq'],
    type: 'middleSchool',
  },
  {
    unit: 'belonging-standard',
    numModules: 15,
    slug: '/lessonPlan/belonging-standard',
    displayName: 'Belonging & Peer Influence',
    subtitle: 'CASEL: Relationship Skills',
    content:
      'Relationship skills and conflict resolution focuses on students developing positive and healthy relationships with diverse individuals and a variety of groups...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U4-Belonging.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    type: 'middleSchool',
    applicationTags: ['standard', 'not-x-lgbtq', 'not-mena'],
  },
  {
    unit: 'belonging-mena',
    numModules: 15,
    slug: '/lessonPlan/belonging-mena',
    displayName: 'Belonging & Peer Influence',
    subtitle: 'CASEL: Relationship Skills',
    content:
      'Relationship skills and conflict resolution focuses on students developing positive and healthy relationships with diverse individuals and a variety of groups...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U4-Belonging.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    applicationTags: ['mena'],
    type: 'middleSchool',
  },
  {
    unit: 'empathy',
    numModules: 15,
    slug: '/lessonPlan/empathy',
    displayName: 'Empathy & Conflict Resolution',
    subtitle: 'CASEL: Social Awareness',
    content:
      'Empathy is the ability to recognize and share the thoughts and feelings of others.  Students learn the process of perspective-taking and the value of empathizing.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U5-Empathy.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    applicationTags: ['x-lgbtq'],
    type: 'middleSchool',
  },
  {
    unit: 'empathy-standard',
    numModules: 15,
    slug: '/lessonPlan/empathy-standard',
    displayName: 'Empathy & Conflict Resolution',
    subtitle: 'CASEL: Social Awareness',
    content:
      'Empathy is the ability to recognize and share the thoughts and feelings of others.  Students learn the process of perspective-taking and the value of empathizing.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U5-Empathy.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    type: 'middleSchool',
    applicationTags: ['standard', 'not-x-lgbtq', 'not-mena'],
  },
  {
    unit: 'empathy-mena',
    numModules: 15,
    slug: '/lessonPlan/empathy-mena',
    displayName: 'Empathy & Conflict Resolution',
    subtitle: 'CASEL: Social Awareness',
    content:
      'Empathy is the ability to recognize and share the thoughts and feelings of others.  Students learn the process of perspective-taking and the value of empathizing.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/Unit-Library_U5-Empathy.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['middleSchool', 'freemium'],
    applicationTags: ['mena'],
    type: 'middleSchool',
  },
  {
    unit: 'futureskills',
    numModules: 9,
    slug: '/lessonPlan/futureskills',
    displayName: 'Future Skills & Planning',
    subtitle: 'CASEL: Responsible Decision Making',
    content:
      'Future planning is the process of considering different life pathways, setting goals and taking intentional steps to work toward a meaningful future. In this unit, students reflect on their values and strengths, explore long-term goals, and learn practical strategies to help them plan ahead with confidence and purpose.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 00_FutureSkills.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['freemium', 'supplementary'],
    type: 'middleSchool',
  },
  {
    unit: 'digitalliteracy',
    numModules: 12,
    slug: '/lessonPlan/digitalliteracy',
    displayName: 'Digital Literacy',
    subtitle: 'CASEL: Responsible Decision Making',
    content:
      'Digital literacy involves understanding how to engage thoughtfully, safely, and critically in digital spaces. In this unit, students explore topics like social media, AI, and screen time to build the skills needed to navigate the digital world in ways that support their well-being and online responsibility.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 00_DigitalLiteracy.png`,
    gradeSpan: [6, 7, 8],
    filterTags: ['freemium', 'supplementary'],
    type: 'middleSchool',
  },
];

export const middleSchoolFreemiumModules: AllCardData[] = [
  {
    content: 'Teacher either presents lesson slides or plays it as a recorded webinar.',
    displayName: 'Introduction to Social Emotional Learning',
    filterTags: ['middleSchool', 'freemium'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 1,
    lessonType: 'Introduction',
    slug: 'slides-intro',
    label: 'Activity',
    subtitle: 'Intro Unit: Slideshow',
    type: 'module',
    unit: 'welcome',
  },
  {
    content: 'Students complete a feelings check-in on the Peekapak check-in wheel.',
    displayName: 'Check In Tool',
    filterTags: ['middleSchool', 'freemium'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 2,
    lessonType: 'Introduction',
    slug: 'check-in-tool',
    label: 'Activity',
    subtitle: 'Intro Unit: Check-In Tool',
    type: 'module',
    unit: 'welcome',
  },
  {
    content:
      'Students will take the pre SELf-assessment and complete an interactive activity with their peers as they begin to learn about the unit skill: Self-awareness.',
    displayName: 'Pre-SELf Assessment',
    filterTags: ['middleSchool', 'freemium'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 1,
    lessonType: 'Introduce',
    slug: 'pre-assessment',
    label: 'Activity',
    subtitle: 'Self-Awareness: Assessment',
    type: 'module',
    unit: 'identity-standard',
  },
  {
    content:
      'Students think about their likes and dislikes. They will see two statements, and will have to pick which one they identify with more!',
    displayName: 'Activity: That’s Me!',
    filterTags: ['middleSchool', 'freemium'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 2,
    lessonType: 'Introduce',
    slug: 'activity-thats-me',
    label: 'Activity',
    subtitle: 'Self-Awareness: Lesson 1',
    type: 'module',
    unit: 'identity-standard',
  },
  {
    content:
      'Students will take the pre SELf-assessment and complete an interactive activity with their peers as they begin to learn about the unit skill: Stress and regulation.',
    displayName: 'Pre-SELf Assessment',
    filterTags: ['middleSchool', 'freemium'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 1,
    lessonType: 'Introduce',
    slug: 'pre-assessment',
    label: 'Activity',
    subtitle: 'Stress and Regulation: Assessment',
    type: 'module',
    unit: 'stress',
  },
  {
    content:
      'Students will have 2 minutes to write the alphabet backwards without writing it forwards first. Without time to prep, tell them to go!',
    displayName: 'Activity: Stress Test',
    filterTags: ['middleSchool', 'freemium'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 2,
    lessonType: 'Introduce',
    slug: 'activity-stress-test',
    label: 'Activity',
    subtitle: 'Stress and Regulation: Lesson 1',
    type: 'module',
    unit: 'stress',
  },
  {
    content:
      'Students will take the pre SELf-assessment and complete an interactive activity with their peers as they begin to learn about the unit skill: Decision making skills.',
    displayName: 'Pre-SELf Assessment',
    filterTags: ['middleSchool', 'freemium'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 1,
    lessonType: 'Introduce',
    slug: 'pre-assessment',
    label: 'Activity',
    subtitle: 'Decision Making Skills: Assessment',
    type: 'module',
    unit: 'decision',
  },
  {
    content:
      'Project activity slides and have students stand up and move to either the left or right side of the room based on their answer. Afterwards, discuss with the class.',
    displayName: 'Activity: This Way or That Way',
    filterTags: ['middleSchool', 'freemium'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 2,
    lessonType: 'Introduce',
    slug: 'activity-this-way-or-that-way',
    label: 'Activity',
    subtitle: 'Decision Making Skills: Lesson 1',
    type: 'module',
    unit: 'decision',
  },
  {
    content:
      'Project activity slides and have students stand up and move to either the left or right side of the room based on their answer. Afterwards, discuss with the class.',
    displayName: 'Pre-SELf Assessment',
    filterTags: ['middleSchool', 'freemium'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 1,
    lessonType: 'Introduce',
    slug: 'pre-assessment',
    label: 'Activity',
    subtitle: 'Belonging: Assessment',
    type: 'module',
    unit: 'belonging',
  },
  {
    content:
      'Students will work in groups of four to write four different collaborative stories based on four prompts.',
    displayName: 'Activity: And Then, What?',
    filterTags: ['middleSchool', 'freemium'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 2,
    lessonType: 'Introduce',
    slug: 'activity-and-then-what',
    label: 'Activity',
    subtitle: 'Belonging: Lesson 1',
    type: 'module',
    unit: 'belonging',
  },
  {
    content:
      'Students will take the pre SELf-assessment and complete an interactive activity with their peers as they begin to learn about the unit skill: Empathy and Conflict Resolution.',
    displayName: 'Pre-SELf Assessment',
    filterTags: ['middleSchool', 'freemium'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 1,
    lessonType: 'Introduce',
    slug: 'pre-assessment',
    label: 'Activity',
    subtitle: 'Empathy & Conflict Resolution: Assessment',
    type: 'module',
    unit: 'empathy',
  },
  {
    content:
      'Students think about their likes and dislikes. They will see two statements, and will have to pick which one they identify with more!',
    displayName: 'Activity: Grocery Shopping at School',
    filterTags: ['middleSchool', 'freemium'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 2,
    lessonType: 'Introduce',
    slug: 'activity-grocery-shopping-at-school',
    label: 'Activity',
    subtitle: 'Empathy & Conflict Resolution: Lesson 1',
    type: 'module',
    unit: 'empathy',
  },
  {
    content: 'Students will discuss factors that influence long-term health and happiness.',
    displayName: 'Continuum Walk',
    filterTags: ['freemium', 'supplementary'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 1,
    lessonType: 'Introduce',
    slug: 'continuum-walk',
    label: 'Activity',
    subtitle: 'Future Skills: Lesson 1',
    type: 'module',
    unit: 'futureskills',
  },
  {
    content: 'Students will discuss two art pieces as a class that convey messages around the digital age.',
    displayName: 'Art and Screens',
    filterTags: ['freemium', 'supplementary'],
    gradeSpan: [6],
    lessonIndex: 1,
    moduleIndex: 1,
    lessonType: 'Introduce',
    slug: 'art-and-screens',
    label: 'Activity',
    subtitle: 'Digital Literacy: Lesson 1',
    type: 'module',
    unit: 'digitalliteracy',
  },
];

export const highSchoolUnits: MiddleSchoolCard[] = [
  {
    unit: 'hswelcome',
    numModules: 7,
    slug: '/lessonPlan/hswelcome',
    displayName: 'Welcome to Peekapak',
    subtitle: 'Overview of CASEL',
    content:
      'Learn about exploring our social and emotional selves, storytelling, perspective-taking, building norms, and working together.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 0_welcome.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool', 'freemium'],
    type: 'highSchool',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    unit: 'hswelcome-mena',
    numModules: 7,
    slug: '/lessonPlan/hswelcome-mena',
    displayName: 'Welcome to Peekapak',
    subtitle: 'Overview of CASEL',
    content:
      'Learn about exploring our social and emotional selves, storytelling, perspective-taking, building norms, and working together.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 0_welcome.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool', 'freemium'],
    type: 'highSchool',
    applicationTags: ['mena'],
  },
  {
    unit: 'hsidentity',
    numModules: 15,
    slug: '/lessonPlan/hsidentity',
    displayName: 'Identity & Self-Awareness',
    subtitle: 'CASEL: Self-Awareness',
    content:
      'Reflect on who you are and how you see yourself by examining how environment, relationships, and personality impact who you are and how you interact with the world.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 1_identity.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool'],
    type: 'highSchool',
    applicationTags: ['x-lgbtq'],
  },
  {
    unit: 'hsidentity-standard',
    numModules: 15,
    slug: '/lessonPlan/hsselfawareness',
    displayName: 'Self-Awareness',
    subtitle: 'CASEL: Self-Awareness',
    content:
      'Reflect on who you are and how you see yourself by examining how environment, relationships, and personality impact who you are and how you interact with the world.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 1_identity.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool', 'freemium'],
    type: 'highSchool',
    applicationTags: ['standard', 'not-x-lgbtq', 'not-mena'],
  },
  {
    unit: 'hsidentity-mena',
    numModules: 15,
    slug: '/lessonPlan/hsselfawareness-mena',
    displayName: 'Self-Awareness',
    subtitle: 'CASEL: Self-Awareness',
    content:
      'Reflect on who you are and how you see yourself by examining how environment, relationships, and personality impact who you are and how you interact with the world.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_Unit-Library_identity_mena.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool', 'freemium'],
    type: 'highSchool',
    applicationTags: ['mena'],
  },
  {
    unit: 'hsstress',
    numModules: 17,
    slug: '/lessonPlan/hsstress',
    displayName: 'Stress and Regulation',
    subtitle: 'CASEL: Self-Management',
    content:
      'Emotional regulation is the ability to manage and express emotions in healthy, constructive ways. Emotional regulation builds upon self-awareness and involves the application of strategies...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 2_stress.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    type: 'highSchool',
    isTrialable: true,
  },
  {
    unit: 'hsstress-mena',
    numModules: 17,
    slug: '/lessonPlan/hsstress-mena',
    displayName: 'Stress and Regulation',
    subtitle: 'CASEL: Self-Management',
    content:
      'Emotional regulation is the ability to manage and express emotions in healthy, constructive ways. Emotional regulation builds upon self-awareness and involves the application of strategies...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 2_stress.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool', 'freemium'],
    applicationTags: ['mena'],
    type: 'highSchool',
    isTrialable: true,
  },
  {
    unit: 'hsdecision',
    numModules: 15,
    slug: '/lessonPlan/hsdecision',
    displayName: 'Decision Making Skills',
    subtitle: 'CASEL: Responsible Decision Making',
    content:
      'Decision making is the process of using best judgement to make a decision and problem solve. In this unit, students learn about the decision making process, how to think critically about a decision...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 3_decision.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool'],
    type: 'highSchool',
    applicationTags: ['x-lgbtq'],
  },
  {
    unit: 'hsdecision-standard',
    numModules: 15,
    slug: '/lessonPlan/hsdecision-standard',
    displayName: 'Decision Making Skills',
    subtitle: 'CASEL: Responsible Decision Making',
    content:
      'Decision making is the process of using best judgement to make a decision and problem solve. In this unit, students learn about the decision making process, how to think critically about a decision...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 3_decision.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool'],
    type: 'highSchool',
    applicationTags: ['standard', 'not-x-lgbtq', 'not-mena'],
  },
  {
    unit: 'hsdecision-mena',
    numModules: 15,
    slug: '/lessonPlan/hsdecision-mena',
    displayName: 'Decision Making Skills',
    subtitle: 'CASEL: Responsible Decision Making',
    content:
      'Decision making is the process of using best judgement to make a decision and problem solve. In this unit, students learn about the decision making process, how to think critically about a decision...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 3_decision.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool'],
    type: 'highSchool',
    applicationTags: ['mena'],
  },
  {
    unit: 'hsbelonging',
    numModules: 15,
    slug: '/lessonPlan/hsbelonging',
    displayName: 'Belonging & Peer Influence',
    subtitle: 'CASEL: Relationship Skills',
    content:
      'Relationship skills and conflict resolution focuses on students developing positive and healthy relationships with diverse individuals and a variety of groups...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 4_belonging.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool'],
    type: 'highSchool',
    applicationTags: ['x-lgbtq'],
  },
  {
    unit: 'hsbelonging-standard',
    numModules: 15,
    slug: '/lessonPlan/hsbelonging-standard',
    displayName: 'Belonging & Peer Influence',
    subtitle: 'CASEL: Relationship Skills',
    content:
      'Relationship skills and conflict resolution focuses on students developing positive and healthy relationships with diverse individuals and a variety of groups...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 4_belonging.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool'],
    type: 'highSchool',
    applicationTags: ['standard', 'not-x-lgbtq', 'not-mena'],
  },
  {
    unit: 'hsbelonging-mena',
    numModules: 15,
    slug: '/lessonPlan/hsbelonging-mena',
    displayName: 'Belonging & Peer Influence',
    subtitle: 'CASEL: Relationship Skills',
    content:
      'Relationship skills and conflict resolution focuses on students developing positive and healthy relationships with diverse individuals and a variety of groups...',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 4_belonging.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool'],
    type: 'highSchool',
    applicationTags: ['mena'],
  },
  {
    unit: 'hsempathy',
    numModules: 15,
    slug: '/lessonPlan/empathy',
    displayName: 'Empathy & Conflict Resolution',
    subtitle: 'CASEL: Social Awareness',
    content:
      'Empathy is the ability to recognize and share the thoughts and feelings of others.  Students learn the process of perspective-taking and the value of empathizing.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 5_conflict.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool'],
    applicationTags: ['standard', 'not-mena'],
    type: 'highSchool',
  },
  {
    unit: 'hsempathy-mena',
    numModules: 15,
    slug: '/lessonPlan/empathy-mena',
    displayName: 'Empathy & Conflict Resolution',
    subtitle: 'CASEL: Social Awareness',
    content:
      'Empathy is the ability to recognize and share the thoughts and feelings of others.  Students learn the process of perspective-taking and the value of empathizing.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 5_conflict.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['highSchool'],
    applicationTags: ['mena'],
    type: 'highSchool',
  },
  {
    unit: 'futureskills',
    numModules: 9,
    slug: '/lessonPlan/futureskills',
    displayName: 'Future Skills & Planning',
    subtitle: 'CASEL: Responsible Decision Making',
    content:
      'Future planning is the process of considering different life pathways, setting goals and taking intentional steps to work toward a meaningful future. In this unit, students reflect on their values and strengths, explore long-term goals, and learn practical strategies to help them plan ahead with confidence and purpose.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 00_FutureSkills.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['freemium', 'supplementary'],
    type: 'highSchool',
  },
  {
    unit: 'digitalliteracy',
    numModules: 12,
    slug: '/lessonPlan/digitalliteracy',
    displayName: 'Digital Literacy',
    subtitle: 'CASEL: Responsible Decision Making',
    content:
      'Digital literacy involves understanding how to engage thoughtfully, safely, and critically in digital spaces. In this unit, students explore topics like social media, AI, and screen time to build the skills needed to navigate the digital world in ways that support their well-being and online responsibility.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 00_DigitalLiteracy.png`,
    gradeSpan: [9, 10, 11, 12],
    filterTags: ['freemium', 'supplementary'],
    type: 'highSchool',
  },
  {
    unit: 'coping-grief',
    numModules: 10,
    slug: '/lessonPlan/coping-grief',
    displayName: 'Coping with Grief and Loss',
    subtitle: '',
    content:
      'Grief and loss are natural responses to many kinds of change, from death to transitions, missed opportunities, and shifts in identity. In this unit, students explore how grief affects us emotionally, socially, and culturally while learning healthy ways to cope, support others, and honor what has been lost. Through discussion, reflection, and creative expression, they build empathy, emotional literacy, and resilience.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 00_Grief.png`,
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    filterTags: ['freemium', 'supplementary'],
    type: 'highSchool',
  },
  {
    unit: 'arabic-calligraphy',
    numModules: 12,
    slug: '/lessonPlan/Arabic-Calligraphy',
    displayName: 'Arabic Calligraphy',
    subtitle: '',
    content:
      'Students explore Arabic calligraphy as a mindful art that connects mind and body, fostering self-regulation through focus, breathwork, and creative expression.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitHeader - 00_Calligraphy_L.png`,
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    filterTags: ['freemium', 'supplementary'],
    applicationTags: ['mena'],
    type: 'highSchool',
  },
  {
    unit: 'gratitude-ramadan',
    numModules: 12,
    slug: '/lessonPlan/Gratitude-Ramadan',
    displayName: 'Gratitude & Ramadan',
    subtitle: '',
    content:
      'Students explore gratitude through Ramadan traditions that emphasize reflection, appreciation, and giving.',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitHeader - 00_Ramadan_L.png`,
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    filterTags: ['freemium', 'supplementary'],
    applicationTags: ['mena'],
    type: 'highSchool',
  },
  {
    unit: 'kindness-dates',
    numModules: 12,
    slug: '/lessonPlan/Kindness-Dates',
    displayName: 'Kindness & Dates',
    subtitle: '',
    content:
      'Students explore the cultural tradition of offering dates as a kind and traditional UAE gesture, guiding students to build relationships, foster community, and create personal and classroom practices of generosity. ',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 00_Kind Dates.png`,
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    filterTags: ['freemium', 'supplementary'],
    applicationTags: ['mena'],
    type: 'highSchool',
  },
  {
    unit: 'empathy-poetry',
    numModules: 12,
    slug: '/lessonPlan/Empathy-Poetry',
    displayName: 'Empathy & Emirati Poetry',
    subtitle: '',
    content:
      'Students explore empathy through the rich oral and written Emirati traditions of poetry, where poets express deep emotion and personal stories. ',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 00_Poetry.png`,
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    filterTags: ['freemium', 'supplementary'],
    applicationTags: ['mena'],
    type: 'highSchool',
  },
  {
    unit: 'teamwork-tolerance',
    numModules: 12,
    slug: '/lessonPlan/Teamwork-Tolerance',
    displayName: 'Teamwork & The Ministry of Tolerance ',
    subtitle: '',
    content:
      'Guided by the UAE Ministry of Tolerance values, this unit explores the skill of teamwork through the context of diversity and collaboration within the United Arab Emirates. ',
    featuredImage: `${getPublicMediaRoot()}images/secondary/HS_UnitLibrary - 00_Tolerance.png`,
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    filterTags: ['freemium', 'supplementary'],
    applicationTags: ['mena'],
    type: 'highSchool',
  },
];

export const highSchoolFreemiumModules: AllCardData[] = [
  {
    content: 'Open the slides and walk through the deck with students, pausing for discussion.',
    displayName: 'SELf Workshops - Slides',
    filterTags: ['highSchool', 'freemium'],
    gradeSpan: [9],
    lessonIndex: 1,
    moduleIndex: 1,
    lessonType: 'Introduction',
    slug: 'slides-workshops',
    label: 'Activity',
    subtitle: 'Intro Unit: Lesson 1',
    type: 'module',
    unit: 'hswelcome',
  },
  {
    content:
      'Students complete a feelings check-in through our check-in wheel. Teachers can view check-in data in the ‘Reports’ tab under Moodboard.',
    displayName: 'Feelings Check-in Wheel',
    filterTags: ['highSchool', 'freemium'],
    gradeSpan: [9],
    lessonIndex: 1,
    moduleIndex: 2,
    lessonType: 'Introduction',
    slug: 'slides-workshops',
    label: 'Activity',
    subtitle: 'Intro Unit: Lesson 1',
    type: 'module',
    unit: 'hswelcome',
  },
  {
    content:
      'Assign students the SELf-Assessment and give students 5 minutes to complete it on their own. Students will learn what their strength is for the Self-Awareness unit.',
    displayName: 'SELf Assessment',
    filterTags: ['highSchool', 'freemium'],
    gradeSpan: [9],
    lessonIndex: 1,
    moduleIndex: 1,
    lessonType: 'Introduce',
    slug: 'self-assessment-standard',
    label: 'Activity',
    subtitle: 'Self-Awareness: SELf Assessment',
    type: 'module',
    unit: 'hsidentity-standard',
  },
  {
    content:
      'Use the slides to facilitate the Boxes and Labels Activity, where students reflect on how labels can stereotype or over-simplify our identities, using celebrities as examples.',
    displayName: 'Activity: Boxes and Labels',
    filterTags: ['highSchool', 'freemium'],
    gradeSpan: [9],
    lessonIndex: 1,
    moduleIndex: 2,
    lessonType: 'Introduce',
    slug: 'activity-boxes-and-labels-standard',
    label: 'Activity',
    subtitle: 'Self-Awareness: Lesson 1',
    type: 'module',
    unit: 'hsidentity-standard',
  },
  {
    content:
      'Assign students the SELf-Assessment and give students 5 minutes to complete it on their own. Students will learn what their strength is for the Stress and Regulation unit.',
    displayName: 'SELf Assessment',
    filterTags: ['highSchool', 'freemium'],
    gradeSpan: [9],
    lessonIndex: 1,
    moduleIndex: 1,
    lessonType: 'Introduce',
    slug: 'self-assessment',
    label: 'Activity',
    subtitle: 'Stress and Regulation: SELf Assessment',
    type: 'module',
    unit: 'hsstress',
  },
  {
    content:
      'Go through the slides and pass out activity sheets to write or draw what is “on our minds” today - what are the things causing us stress and worry that are taking up space in our brains.',
    displayName: 'Activity: What’s on your Mind?',
    filterTags: ['highSchool', 'freemium'],
    gradeSpan: [9],
    lessonIndex: 1,
    moduleIndex: 2,
    lessonType: 'Introduce',
    slug: 'activity-whats-on-your-mind',
    label: 'Activity',
    subtitle: 'Stress and Regulation: Lesson 1',
    type: 'module',
    unit: 'hsstress',
  },
  {
    content: 'Students will discuss factors that influence long-term health and happiness.',
    displayName: 'Continuum Walk',
    filterTags: ['freemium', 'supplementary'],
    gradeSpan: [9],
    lessonIndex: 1,
    moduleIndex: 1,
    lessonType: 'Introduce',
    slug: 'continuum-walk',
    label: 'Activity',
    subtitle: 'Future Skills: Lesson 1',
    type: 'module',
    unit: 'futureskills',
  },
  {
    content: 'Students will discuss two art pieces as a class that convey messages around the digital age.',
    displayName: 'Art And Screens',
    filterTags: ['freemium', 'supplementary'],
    gradeSpan: [9],
    lessonIndex: 1,
    moduleIndex: 1,
    lessonType: 'Introduce',
    slug: 'art-and-screens',
    label: 'Activity',
    subtitle: 'Digital Literacy: Lesson 1',
    type: 'module',
    unit: 'digitalliteracy',
  },
  // TO DO: Add in the rest of the modules when U3-5 are ready
  // {
  //   content:
  //     'Assign students the SELf-Assessment and give students 5 minutes to complete it on their own. Students will learn what their strength is for the Decision Making Skills unit.',
  //   displayName: 'SELf Assessment',
  //   filterTags: ['highSchool', 'freemium'],
  //   gradeSpan: [9],
  //   lessonIndex: 1,
  //   moduleIndex: 1,
  //   lessonType: '',
  //   slug: '',
  //   label: 'Activity',
  //   subtitle: 'Decision Making Skills: SELf Assessment',
  //   type: 'module',
  //   unit: 'hsdecision',
  // },
  // {
  //   content:
  //     'Use the slides to facilitate the Partial Picture Game, where students make decisions for a character in a scenario - without having all the information!',
  //   displayName: 'Activity: Partial Picture Game',
  //   filterTags: ['highSchool', 'freemium'],
  //   gradeSpan: [9],
  //   lessonIndex: 1,
  //   moduleIndex: 2,
  //   lessonType: '',
  //   slug: '',
  //   label: 'Activity',
  //   subtitle: 'Decision Making Skills: Lesson 1',
  //   type: 'module',
  //   unit: 'hsdecision',
  // },
  // {
  //   content:
  //     'Assign students the SELf-Assessment and give students 5 minutes to complete it on their own. Students will learn what their strength is for the Decision Making Skills unit.',
  //   displayName: 'SELf Assessment',
  //   filterTags: ['highSchool', 'freemium'],
  //   gradeSpan: [9],
  //   lessonIndex: 1,
  //   moduleIndex: 1,
  //   lessonType: '',
  //   slug: '',
  //   label: 'Activity',
  //   subtitle: 'Belonging & Peer Influence: SELf Assessment',
  //   type: 'module',
  //   unit: 'hsbelonging',
  // },
  // {
  //   content:
  //     'Use the slides to facilitate the Partial Picture Game, where students make decisions for a character in a scenario - without having all the information!',
  //   displayName: 'Activity: Partial Picture Game',
  //   filterTags: ['highSchool', 'freemium'],
  //   gradeSpan: [9],
  //   lessonIndex: 1,
  //   moduleIndex: 2,
  //   lessonType: '',
  //   slug: '',
  //   label: 'Activity',
  //   subtitle: 'Belonging & Peer Influence: Lesson 1',
  //   type: 'module',
  //   unit: 'hsbelonging',
  // },
  // {
  //   content:
  //     'Assign students the SELf-Assessment and give students 5 minutes to complete it on their own. Students will learn what their strength is for the Belonging and Peer Influence unit.',
  //   displayName: 'SELf Assessment',
  //   filterTags: ['highSchool', 'freemium'],
  //   gradeSpan: [9],
  //   lessonIndex: 1,
  //   moduleIndex: 1,
  //   lessonType: '',
  //   slug: '',
  //   label: 'Activity',
  //   subtitle: 'Empathy & Conflict Resolution: SELf Assessment',
  //   type: 'module',
  //   unit: 'hsempathy',
  // },
  // {
  //   content:
  //     'Use the slides to facilitate the ‘Sharks in the Water’ Game, where students have to get into groups (quickly!) to stay safe from imaginary sharks. Students then reflect on what it feels like to be included and excluded.',
  //   displayName: 'Activity: What do You See?',
  //   filterTags: ['highSchool', 'freemium'],
  //   gradeSpan: [9],
  //   lessonIndex: 1,
  //   moduleIndex: 2,
  //   lessonType: '',
  //   slug: '',
  //   label: 'Activity',
  //   subtitle: 'Empathy & Conflict Resolution: Lesson 1',
  //   type: 'module',
  //   unit: 'hsempathy',
  // },
];

export const videoLessons: VideoCard[] = [
  {
    displayName: 'Self-Regulation',
    subtitle: 'Video Lesson | 8:09 mins',
    assetId: `rcTU02oIGvBgc7FTW01ebD02ku00umnebbNsf5pFgaRGvhk`,
    content: `Leo the Hedgehog feels nervous about his first day of school. Papa Hedgehog teaches Leo a poem about the value of sharing one's feelings with others. This poem helps Leo feel better.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/self-regulationVid.png`,
    filterTags: ['video', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
    isTrialable: true,
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Respect',
    subtitle: 'Video Lesson | 7:57 mins',
    assetId: `jg2BP6wa902mpkKMJbbMkHUtYcZ02Ku015CsR2PQ6Ac102E`,
    content: `It's the Peekaville Elementary School Berryball Championship Game! Zoey learns that to be a good team leader, she must treat her teammates with kindness and respect.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/respectVid.png`,
    filterTags: ['video'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Gratitude',
    subtitle: 'Video Lesson | 8:00 mins',
    assetId: `02v9SQ1kbeJxnoR2XqB3D2op1vqUVjiF5YrmoSkZbbog`,
    content: `Cody is in a grumpy mood. It’s rainy and cold and nothing is going as planned. Later, he discovers a mysterious tree that reveals to him that there’s actually a lot for which to be thankful.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/gratitudeVid.png`,
    filterTags: ['video'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Perseverance',
    subtitle: 'Video Lesson | 8:34 mins',
    assetId: `q6xpPnVcDN9QlwIYWYqC65X008oE9ehIBIaHa01XMOEx4`,
    content: `Brady is holding auditions for his new band and Cody wants to try out. Even though it's not easy, he works hard to learn the drums in time for the big show.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/perseveranceVid.png`,
    filterTags: ['video'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Empathy',
    subtitle: 'Video Lesson | 7:32 mins',
    assetId: `w02b8EpY3ayw01DZZmjsJWFGdvxgeint3Re01HM1OhAMyQ`,
    content: `There’s a new student in Peekaville and all the Peekapals are abuzz with excitement. Despite their excitement the new student doesn’t want to hear about her new home so the pals need to learn to see things from her perspective.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/empathyVid.png`,
    filterTags: ['video'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Teamwork',
    subtitle: 'Video Lesson | 7:43 mins',
    assetId: `s6AcHr007xaYbf01021cM9hKw1FpeljkFg4S1mUcAgeUgw`,
    content: `The Peekapals go on an adventure to help Apollo and Sebastian perfect their newest invention.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/teamworkVid.png`,
    filterTags: ['video'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Kindness',
    subtitle: 'Video Lesson | 8:18 mins',
    assetId: `00ve9UOrgatBIGiUYtcpFwHu7YxJ7zcjc2IOn6RgzrSU`,
    content: `All the Peekapals are preparing for the Winter Wonderlude celebration with their family and friends. Throughout the day Kenji helps everyone prepare and all the Peekapals help him prepare in return.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/kindnessVid.png`,
    filterTags: ['video'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Honesty',
    subtitle: 'Video Lesson | 8:55 mins',
    assetId: `Y0200axr3QwrkRtVJVFxrRyS2iinzVIe1ke6QS2ZOXk3w`,
    content: `There’s a big presentation coming up at Peekaville Elementary and everyone is getting ready. Unfortunately, Menka doesn’t have her presentation ready and she needs to decide if she will tell her teacher, and her friends, the truth.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/honestyVid.png`,
    filterTags: ['video'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Optimism',
    subtitle: 'Video Lesson | 10:33 mins',
    assetId: `azSt55ivtprEiV9QU3tzMb3YRXEy3aciNb02BbcfH5wE`,
    content: `While gardening with her Dad, Lucia finds a secret box with hints leading to Reginald Peekapork’s lost treasure. Lucia spends the day tracking down the treasure despite a few bumps in the road.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/optimismVid.png`,
    filterTags: ['video'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Courage',
    subtitle: 'Video Lesson | 9:41 mins',
    assetId: `202ckUOZGc02yCC6P9XiTacUr9gCO9na8S1yep1vGWO2U`,
    content: `Sebastian the Salamander is having a big pool party for all of his pals. Apollo can’t swim and doesn’t want to go to the party so he has to decide if he will brave the water to be with his friends.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/courageVid.png`,
    filterTags: ['video'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Managing Emotions Part 1 (Gr. K-1)',
    subtitle: 'Activity | 14:49 mins',
    assetId: `Y00JruViBE9QiQa01tQhS4AsZujxNITJVEu29BMMJayAQ`,
    content: `This session will get students up and moving and practice different calming strategies. This session will guide students through breathing and yoga exercises.  (K-1st grades). `,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Emotions1.jpeg`,
    filterTags: ['video'],
    gradeSpan: [-1, 0, 1],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Managing Emotions Part 2 (Gr. K-1)',
    subtitle: 'Activity | 23:01 mins',
    assetId: `ROASJU2bO89aQ1npaxfICdfnI6vcMiZaTAFmt1IdZms`,
    content: `This session is a continuation to reinforce skills learned in part 1.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Emotions2.jpeg`,
    filterTags: ['video'],
    gradeSpan: [-1, 0, 1],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Developing Community Part 1 (Gr. 2-3)',
    subtitle: 'Activity | 12:29 mins',
    assetId: `8sCNQCbOra6H8idGvvx1y02vpGFJOFSx2x9gS02v1dbNc`,
    content: `This interactive session guides students to create art that highlights their pride in their local community/school and what it means to them. (2nd to 3rd grades)`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Community1.jpeg`,
    filterTags: ['video'],
    gradeSpan: [2, 3],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Developing Community Part 2 (Gr. 2-3)',
    subtitle: 'Activity | 10:08 mins',
    assetId: `tyw6T8kgi6vGSVFsMmNEliKVbVflBXcvQ4UzeIzcncw`,
    content: `This session is a continuation to reinforce skills learned in part 1.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Community2.jpeg`,
    filterTags: ['video'],
    gradeSpan: [2, 3],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Leadership and Communication Part 1 (Gr. 4-5)',
    subtitle: 'Activity | 10:16 mins',
    assetId: `j2G3R1RnP9kedh41W9Pe68P1xtN1YgS5hCVf9702hAxI`,
    content: `This session encourages students to get more involved in school and in the community to make a difference. (4th to 5th grades)`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Leadership1.jpeg`,
    filterTags: ['video'],
    gradeSpan: [4, 5],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Leadership and Communication Part 2 (Gr. 4-5)',
    subtitle: 'Activity | 11:00 mins',
    assetId: `MvMvMYgG6POzOHPBtjH9pEiMURiECvXvpWLxUs00tSk8`,
    content: `This session is a continuation to reinforce skills learned in part 1.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Leadership2.jpeg`,
    filterTags: ['video'],
    gradeSpan: [4, 5],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Mediating Media Use Part 1 (Gr. 4-5)',
    subtitle: 'Activity | 15:10 mins',
    assetId: `Eg01wX01k9H8lUx4lKpX00rsC1DQaJbCAgu5DycUt00rdkA`,
    content: `A practical and positive approach to guide students on their digital well-being. This 15 min. session encourages students with interactive activities to reflect on when, where, how and with who they use devices with to mediate their media use.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Media1.jpeg`,
    filterTags: ['video'],
    gradeSpan: [4, 5],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Mediating Media Use Part 2 (Gr. 4-5)',
    subtitle: 'Activity | 13:26 mins',
    assetId: `8HfdWom00TB26902X02oKtorhjUIOHAjeGBag9ABoEkcos`,
    content: `This session is a continuation to reinforce skills learned in part 1.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Media2.jpeg`,
    filterTags: ['video'],
    gradeSpan: [4, 5],
    type: 'video',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Peekapak Theme Song',
    subtitle: 'Theme Song | 2:42 mins',
    assetId: `kl00S8cN8wSuWicHO01xt6vIt01Ty900NjT8XuGzKj5RJ28`,
    content: `Play our theme song as background music, during morning meetings, during independent work time and more!`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Peekapak Album Cover.jpg`,
    filterTags: ['music', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
    isTrialable: true,
  },
  {
    displayName: 'Peekapak Theme Song (Acoustic)',
    subtitle: 'Theme Song (Acoustic) | 2:29 mins',
    assetId: `MHzSwnAwpQORUw02gtsX5014jS02DSLAwYM9MywxgE8Aqg`,
    content: `Play our theme song as background music, during morning meetings, during independent work time and more!`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Peekapak Album Cover.jpg`,
    filterTags: ['music', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
    isTrialable: true,
  },
  {
    displayName: 'Self Regulation | My Feelings',
    subtitle: 'Music Lesson | 2:50 mins',
    unit: 'Self-Regulation',
    assetId: `Of5STxFi73HbrJJAbqvpta1CC00a65NaH4SUjzixeqaE`,
    content: `Discover the importance of sharing your feelings and recognizing your emotions. Includes Walkthrough of lyrics before song.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/self-regulationVid.png`,
    filterTags: ['music', 'freemium'],
    applicationTags: ['music-joanna', 'standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
    isTrialable: true,
  },
  {
    displayName: 'Respect | Respectful Friends',
    subtitle: 'Music Lesson | 1:27 mins',
    unit: 'Respect',
    assetId: `8khHR009fgM721n5P7QLIiUcxEkXzJNzEM1CmNz00DuRM`,
    content: `Learn about the importance of respecting the differences and uniqueness of our friends.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/respectVid.png`,
    filterTags: ['music'],
    applicationTags: ['music-joanna', 'standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
  },
  {
    displayName: 'Gratitude | Gratitude Tree',
    subtitle: 'Music Lesson | 2:12 mins',
    unit: 'Gratitude',
    assetId: `Jx6luvmi6eQQrXW3h2pGejkWQIIhBwErZk3v02VkVXng`,
    content:
      'Learn about the different ways we can all show gratitude to each other. Includes walkthrough of lyrics before song.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/gratitudeVid.png`,
    filterTags: ['music'],
    applicationTags: ['music-joanna', 'standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
  },
  {
    displayName: 'Perseverance | Joyful Sound',
    subtitle: 'Music Lesson | 1:52 mins',
    unit: 'Perseverence',
    assetId: `IrHOyXGQZyLtxJbTU01yoYEzHD3yf7gg500jFvUbDcRYU`,
    content: `Dance along with ‘A Joyful Sound’ - A chance to laugh, sing, move and dance!`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/perseveranceVid.png`,
    filterTags: ['music'],
    applicationTags: ['music-joanna', 'standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
  },
  {
    displayName: 'Empathy | Inés and Leo',
    subtitle: 'Music Lesson | 1:54 mins',
    unit: 'Empathy',
    assetId: `LdEHz5ubeZzPkrDUBor01wuituV02E5Bdl1TjxpOdw7o00`,
    content: `Discover how empathy can be a powerful tool from Inés' experience on her first day of school.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/empathyVid.png`,
    filterTags: ['music'],
    applicationTags: ['music-joanna', 'standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
  },
  {
    displayName: 'Teamwork | Building a Robot',
    subtitle: 'Music Lesson | 1:16 mins',
    unit: 'Teamwork',
    assetId: `4oP00rkrLAqut5fJKrRrqCAcm557gp4bmEvuOhSyPUKs`,
    content: `Learn about how planning, teamwork and perseverance helps when taking on new challenges.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/teamworkVid.png`,
    filterTags: ['music'],
    applicationTags: ['music-joanna', 'standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
  },
  {
    displayName: 'Kindness | Wonderlude Poem',
    subtitle: 'Music Lesson | 1:28 mins',
    unit: 'Kindness',
    assetId: `DKwP01GvA3pT1LmUmWJQrsLN5zWVC3kGPbJOD012dNhUM`,
    content: 'Learn about how kindness spreads throughout Peekaville during their Winter Wonderlude.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/kindnessVid.png`,
    filterTags: ['music'],
    applicationTags: ['music-joanna', 'standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
  },
  {
    displayName: 'Honesty | Telling the Truth',
    subtitle: 'Music Lesson | 1:36 mins',
    unit: 'Honesty',
    assetId: `66c00ZjZph51jpmtyyBEAiLeH5yoFZH8db7qNlKjQIEI`,
    content: 'Establish the key connections between telling the truth and gaining trust.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/honestyVid.png`,
    filterTags: ['music'],
    applicationTags: ['music-joanna', 'standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
  },
  {
    displayName: `Optimism | Lucia's Treasure`,
    subtitle: 'Music Lesson | 1:42 mins',
    unit: 'Optimism',
    assetId: `YyhNVi02IoPiM02W2Hz84kfUEXbsHJHpESW100up1Lg0074`,
    content:
      'Follow Lucia on her treasure hunt where she learns the importance of thinking positively and believing in yourself.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/optimismVid.png`,
    filterTags: ['music'],
    applicationTags: ['music-joanna', 'standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
  },
  {
    displayName: 'Courage | Apollo the Brave',
    subtitle: 'Music Lesson | 2:22 mins',
    unit: 'Courage',
    assetId: `800YRIdDlORjCJjGdWvuKOJZ8712C47y302W6xHZqa36U`,
    content: `Learn how Apollo faces his fear of swimming at Sebastian's birthday party.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/courageVid.png`,
    filterTags: ['music'],
    applicationTags: ['music-joanna', 'standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
  },
  {
    displayName: 'Papa Hedgehog’s Pandemic Poem',
    subtitle: 'Music Lesson | 8:18 mins',
    assetId: `p8hAnhJ9uTx01RPBRFcN1Yxt7xs02v02KW9gEqEg8vzPNQ`,
    content: `This song is from our book “The Peekapak Pals go Back to School with the Peekaflu.” In the book, Leo the Hedgehog feels nervous about going back to school amidst the Peekaflu. Papa Hedgehog teaches Leo a poem about the value of sharing one's feelings with others.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/papa-poem.png`,
    filterTags: ['music'],
    applicationTags: ['music-joanna', 'standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
  },
  {
    displayName: 'Leo’s Pandemic Poem',
    subtitle: 'Music Lesson | 7:06 mins',
    assetId: `ARGDefSe9IrJIVAVSOG8NV4exFxY1tkBvlcc01E9QJmA`,
    content:
      'This song is from our book “The Peekapak Pals go Back to School with the Peekaflu.” In the book, Leo the Hedgehog feels nervous about going back to school amidst the Peekaflu. To help his classmates remember all the new rules, Leo creates a poem to help remind everyone.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/leo-poem.png`,
    filterTags: ['music'],
    applicationTags: ['music-joanna', 'standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'video',
  },
];

export const linkCards: LinkCard[] = [
  {
    displayName: 'May Activity Calendar (PK-5)',
    subtitle: 'Extras',
    slug: Documents.selActivityCalendarPkTo5,
    content:
      'Engage your class with a monthly wellbeing Activity Calendar! During May, we focus on gratitude and friendship. Print out the calendar or project it to complete activities during class time.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/SELActivityCalendars/May+2026+Elementary.png`,
    filterTags: ['extras', 'freemium', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'May Activity Calendar (6-12)',
    subtitle: 'Extras',
    slug: Documents.selActivityCalendarG6To12,
    content:
      'Engage your class with a monthly wellbeing Activity Calendar! During May, we focus on Empathy & Conflict Resolution activities. Print out the calendar or project it to complete activities during class time.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/SELActivityCalendars/May+2026+Secondary.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'May Activity Calendar (PK-5)',
    subtitle: 'Extras',
    slug: Documents.selActivityCalendarPkTo5Mena,
    content:
      'Engage your class with a monthly wellbeing Activity Calendar! During May, we focus on gratitude and friendship. Print out the calendar or project it to complete activities during class time.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/SELActivityCalendars/MENA+May+2026+Elementary.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'May Activity Calendar (6-12)',
    subtitle: 'Extras',
    slug: Documents.selActivityCalendarG6To12Mena,
    content:
      'Engage your class with a monthly wellbeing Activity Calendar! During May, we focus on Empathy & Conflict Resolution activities. Print out the calendar or project it to complete activities during class time.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/SELActivityCalendars/MENA+May+2026+Secondary.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Peekapak Toolkit',
    subtitle: 'Extras',
    slug: Documents.schoolAdminToolKit,
    content:
      'This toolkit is your guide to all Peekapak has to offer! It contains helpful signs, activities, morning announcements, PD opportunities and more!',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Admin-Toolkit-Thumbnail.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Peekapak Toolkit',
    subtitle: 'Extras',
    slug: Documents.schoolAdminToolKitMENA,
    content:
      'This toolkit is your guide to all Peekapak has to offer! It contains helpful signs, activities, morning announcements, PD opportunities and more!',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Admin-Toolkit-Thumbnail-MENA.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Peekapak Certificates of Achievement [AR]',
    subtitle: 'Extras',
    slug: Documents.CertificatesOfAchievementAR,
    content:
      "Recognize your students' wellbeing achievements by awarding them with our bilingual Peekapak certificates of achievement! Available in Arabic and English.",
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/CertificationofAchievementAR.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Ramadan Gratitude Tags',
    subtitle: 'Extras',
    slug: Documents.RamadanGratitudeTags,
    content: 'Help students express gratitude during Ramadan with our special edition gratitude tags!',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Ramadan+Tags_v1.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Emotion Cards',
    subtitle: 'Extras',
    slug: Documents.emotionCards,
    content: 'Help students review their emotions in a meaningful way through our printable emotion flashcards.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/EmotionCards.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Emotion Cards (Arabic)',
    subtitle: 'Extras',
    slug: Documents.emotionCardsArabic,
    content:
      'Help students review their emotions in Arabic, in a meaningful way through our printable emotion flashcards.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/EmotionCardsArabic.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Feelings Check-In Poster',
    subtitle: 'Extras',
    slug: Documents.FeelingsPoster,
    content:
      'Help students quickly and easily identify their emotions with our feelings check-in poster. Great to hang up in your classroom or calm corner.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/K-5 Feelings Check-in Poster.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'National Identity Unit',
    subtitle: 'Extras',
    slug: Documents.nationalIdentity,
    content:
      'Help students learn about their National Identity, including culture, heritage, community and values. Students create a National Identity circle to visualize who they are!',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/nationalidentity.jpg`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'National Identity Unit (English)',
    subtitle: 'Extras',
    slug: Documents.nationalIdentityEnglish,
    content:
      'Help students learn about their National Identity, including culture, heritage, community and values. Students create a National Identity circle to visualize who they are!',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/nationalidentityEnglish.jpg`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Learning from Nature',
    subtitle: 'Extras',
    slug: Documents.cop28K2,
    content:
      'This special COP28 lesson plan will help students learn how to use nature to help them feel better when they are sad or upset.',
    featuredImage: `${getPublicMediaRoot()}Cop28/cop28K2.jpg`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [-1, 0, 1, 2],
    buttonText: 'View',
    type: 'link',
  },
  {
    displayName: 'Respecting Nature',
    subtitle: 'Extras',
    slug: Documents.cop2835,
    content:
      'This special COP28 lesson plan will help students learn about respect and what it looks like to respect our environment.',
    featuredImage: `${getPublicMediaRoot()}Cop28/cop2835.jpg`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [3, 4, 5],
    buttonText: 'View',
    type: 'link',
  },
  {
    displayName: 'Climate Change: Anxiety and Action',
    subtitle: 'Extras',
    slug: Documents.cop28MS,
    content:
      'This special COP28 lesson plan will help students reflect on how the climate crisis and related issues affect their level of stress and wellbeing on a day-to-day basis.',
    featuredImage: `${getPublicMediaRoot()}Cop28/cop28MS.jpg`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [6, 7, 8],
    buttonText: 'View',
    type: 'link',
  },
  {
    displayName: 'Climate Change: Anxiety and Action',
    subtitle: 'Extras',
    slug: Documents.cop28HS,
    content:
      'This special COP28 lesson plan will help students reflect on how the climate crisis and related issues affect their level of stress and wellbeing on a day-to-day basis.',
    featuredImage: `${getPublicMediaRoot()}Cop28/cop28HS.jpg`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
  },
  {
    displayName: 'Getting Started with Peekapak (PK-5)',
    subtitle: 'User Guides',
    slug: Documents.getStartedPeekapakPKto5,
    content: `Our comprehensive getting started guide for PK-5 will walk you through everything you need to know about Peekapak.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/gettingstarted-pk5.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'Getting Started with Peekapak (6-8)',
    subtitle: 'User Guides',
    slug: Documents.getStartedPeekapakGrade6to8,
    content: `Our comprehensive getting started guide for grades 6-8 will walk you through everything you need to know to get started with Peekapak's secondary curriculum.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/gettingstarted-68.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [6, 7, 8],
    type: 'link',
  },
  {
    displayName: 'Story Reading Levels',
    subtitle: 'User Guides',
    slug: Documents.readingLevelsMS,
    content:
      'Our stories are offered at a variety of reading levels throughout each unit to best suit students needs. This document shows the Lexile level, F+P level and the reading level by grade.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/ReadingLevelMS-Thumbnail.png`,
    filterTags: ['howTo', 'freemium'],
    gradeSpan: [6, 7, 8],
    buttonText: 'Explore',
    type: 'link',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Getting Started with Peekapak (9-12)',
    subtitle: 'User Guides',
    slug: Documents.getStartedPeekapakGrade9to12,
    content: `Our comprehensive getting started guide for grades 9-12 will walk you through everything you need to know to get started with Peekapak's secondary curriculum.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/gettingstarted-912.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [9, 10, 11, 12],
    type: 'link',
  },
  {
    displayName: 'Story Reading Levels',
    subtitle: 'User Guides',
    slug: Documents.readingLevelsHS,
    content:
      'Our stories are offered at a variety of reading levels throughout each unit to best suit students needs. This document shows the Lexile level, F+P level and the reading level by grade.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/ReadingLevelHS-Thumbnail.png`,
    filterTags: ['howTo', 'freemium'],
    gradeSpan: [9, 10, 11, 12],
    buttonText: 'Explore',
    type: 'link',
    applicationTags: ['standard', 'x-lgbtq', 'not-mena'],
  },
  {
    displayName: 'Getting Started with myPeekaville',
    subtitle: 'User Guides',
    slug: Documents.getStartedmyPeekaville,
    content: `Our comprehensive getting started guide for myPeekaville will walk you through everything you need to know about our SEL learning game, myPeekaville.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/gettingstarted-mypeekaville.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'K-5 Teacher Map',
    subtitle: 'User Guides',
    slug: Documents.peekapakElementaryTeacherConsoleMap,
    content: `Learn everything you need to know about the Elementary library page through our console map. Including navigation, myPeekaville locking/unlocking, filtering, switching classes and connecting parents.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/peekapak-elementary-teacher-console-map.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'MTSS & Peekapak',
    subtitle: 'User Guides',
    slug: Documents.MTSSAlignmentAndPeekapak,
    content: `Peekapak can be used as an additional support to provide intervention and instruction for all students.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Peekapak MTSS Alignment.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'MTSS & Peekapak',
    subtitle: 'User Guides',
    slug: Documents.MTSSAlignmentAndPeekapak,
    content: `Peekapak can be used as an additional support to provide intervention and instruction for all students.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Peekapak MTSS Alignment.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    type: 'link',
  },
  {
    displayName: 'K-5 Student Map',
    subtitle: 'User Guides',
    slug: Documents.myPeekavilleStudentConsoleMap,
    content: `Learn everything you need to know about myPeekaville through our console map. Including navigation, avatar customization, how to spend berries, completing quests, playing mini games and more.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/myPeekaville-teacher-console-map.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: '6-12 Teacher Map',
    subtitle: 'User Guides',
    slug: Documents.peekapakMiddleSchoolTeacherConsoleMap,
    content: `Learn everything educators need to know about the Secondary library page through our console map. Including navigation, filtering, switching classes and rostering students.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/peekapak-middle-school-teacher-console-map.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    type: 'link',
  },
  {
    displayName: '6-12 Student Map',
    subtitle: 'User Guides',
    slug: Documents.peekapakMiddleSchoolStudentConsoleMap,
    content: `Learn everything students need to know about the Secondary library page through our console map. Including access To Do lists, filtering by state and how to complete assignments.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/peekapak-middle-school-student-console-map.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    type: 'link',
  },
  {
    displayName: 'Guided Reading Levels for 6-8',
    subtitle: 'User Guides',
    slug: Documents.peekapakHumanInterestStoriesGuidedReadingLevels,
    content:
      'Levelling evaluation conducted by a reading recovery expert. A variety of stories are available, providing choice and flexibility to choose narratives that are best suited for your student population.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/peekapak-human-interest-stories-guided-reading-levels.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [6, 7, 8],
    type: 'wip', // hiding temporarily until file is edited for content. Was 'link'
  },
  {
    displayName: 'PK-5 Scope and Sequence',
    subtitle: 'User Guides',
    slug: Documents.pkTo5ScopeAndSequence,
    content: 'Learn how we recommend pacing our units and lesson plans in our recommended scope and sequence for PK-5.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/EL-Scope-and-Sequence.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'Gr. 6-8 Scope and Sequence',
    subtitle: 'User Guides',
    slug: Documents.MSScopeAndSequence,
    content:
      'Learn how we recommend pacing our units and lesson plans in our recommended scope and sequence for grade 6-8.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/MS-Scope-and-Sequence.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [6, 7, 8],
    type: 'link',
  },
  {
    displayName: 'Gr. 9-12 Scope and Sequence',
    subtitle: 'User Guides',
    slug: Documents.HSScopeAndSequence,
    content:
      'Learn how we recommend pacing our units and lesson plans in our recommended scope and sequence for grade 9-12.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/HS-Scope-and-Sequence.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [9, 10, 11, 12],
    type: 'link',
  },
  {
    displayName: 'Peekapak Implementation Guide',
    subtitle: 'User Guides',
    slug: Documents.ImplementationGuide,
    content:
      "Get the most out of Peekapak with our step-by-step Implementation Guide. Whether you're just getting started or looking to deepen your impact, this resource helps educators and school leaders integrate Peekapak into classrooms with ease—covering setup, planning, lesson delivery, and progress tracking.",
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Peekapak+Implementation+Guide+-+EXTERNAL.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    type: 'link',
  },
  {
    displayName: 'Parent Information Pack',
    subtitle: 'User Guides',
    slug: Documents.parentInfoPack,
    content:
      'If families are looking to learn more about Peekapak, this booklet walks them through the core components of the PK-12 curriculum. Feel free to print or email it to families.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Parent+Information+Pack.png`,
    filterTags: ['howTo', 'freemium'],
    buttonText: 'Explore',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    type: 'link',
  },
  {
    displayName: 'Self-Regulation Poster',
    unit: 'Self-Regulation',
    subtitle: 'Self Regulation Unit Poster',
    slug: Documents.posterSelfRegulation,
    content: `Reinforce self-regulation concepts taught in class by printing out our self-regulation unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/SelfReg_Poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Self-Regulation Poster',
    unit: 'Self-Regulation',
    subtitle: 'Self Regulation Unit Poster',
    slug: Documents.posterSelfRegulationUAE,
    content: `Reinforce self-regulation concepts taught in class by printing out our self-regulation unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/SelfReg_Poster_UAE.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Self-Regulation Poster (Arabic)',
    unit: 'Self-Regulation',
    subtitle: 'Arabic Self Regulation Unit Poster',
    slug: Documents.posterSelfRegulationArabic,
    content: `Reinforce self-regulation concepts taught in class by printing out our Arabic self-regulation poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/self-regulationArabic.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Self-Regulation Spanish',
    unit: 'Self-Regulation',
    subtitle: 'Spanish Self Regulation Unit Poster',
    slug: Documents.posterSelfRegulationSpanish,
    content: `Reinforce self-regulation concepts taught in class by printing out our self-regulation unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/self-regulationES.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Respect Poster',
    unit: 'Respect',
    subtitle: 'Respect Unit Poster',
    slug: Documents.posterRespect,
    content: `Reinforce respectful language in your classroom by printing out our respect unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Respect_Poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Respect Poster',
    unit: 'Respect',
    subtitle: 'Respect Unit Poster',
    slug: Documents.posterRespectUAE,
    content: `Reinforce respectful language in your classroom by printing out our respect unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Respect_Poster_UAE.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Respect Poster (Arabic)',
    unit: 'Respect',
    subtitle: 'Arabic Respect Unit Poster',
    slug: Documents.posterRespectArabic,
    content: `Reinforce respectful language in your classroom by printing out our Arabic respect poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/respectArabic.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Respect Spanish',
    unit: 'Respect',
    subtitle: 'Spanish Respect Unit Poster',
    slug: Documents.posterRespectSpanish,
    content: `Reinforce respectful language in your classroom by printing out our respect unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/respectES.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Gratitude Poster',
    unit: 'Gratitude',
    subtitle: 'Gratitude Unit Poster',
    slug: Documents.posterGratitude,
    content: `Reinforce gratitude strategies in your class by printing out our gratitude unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Gratitude_Poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'Gratitude Poster (Arabic)',
    unit: 'Gratitude',
    subtitle: 'Arabic Gratitude Unit Poster',
    slug: Documents.posterGratitudeArabic,
    content: `Reinforce gratitude strategies in your class by printing out our Arabic gratitude poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/gratitudeArabic.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Gratitude Spanish',
    unit: 'Gratitude',
    subtitle: 'Spanish Gratitude Unit Poster',
    slug: Documents.posterGratitudeSpanish,
    content: `Reinforce gratitude strategies in your class by printing out our gratitude unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/gratitudeES.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Perseverance Poster',
    unit: 'Perseverance',
    subtitle: 'Perseverance Unit Poster',
    slug: Documents.posterPerseverance,
    content: `Help students show perseverance in your class by printing out our perseverance poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Perseverance_Poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Perseverance Poster',
    unit: 'Perseverance',
    subtitle: 'Perseverance Unit Poster',
    slug: Documents.posterPerseveranceUAE,
    content: `Help students show perseverance in your class by printing out our perseverance poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Perseverance_Poster_UAE.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Perseverance Poster (Arabic)',
    unit: 'Perseverance',
    subtitle: 'Arabic Perseverance Unit Poster',
    slug: Documents.posterPerseveranceArabic,
    content: `Help students show perseverance in your class by printing out our Arabic perseverance poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/perseveranceArabic.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Perseverance Spanish',
    unit: 'Perseverance',
    subtitle: 'Spanish Perseverance Unit Poster',
    slug: Documents.posterPerseveranceSpanish,
    content: `Help students show perseverance in your class by printing out our perseverance poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/perseveranceES.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Empathy Poster',
    unit: 'Empathy',
    subtitle: 'Empathy Unit Poster',
    slug: Documents.posterEmpathy,
    content: `Remind students the importance of using empathy in the classroom by printing out our empathy unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Empathy_Poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Empathy Poster',
    unit: 'Empathy',
    subtitle: 'Empathy Unit Poster',
    slug: Documents.posterEmpathyUAE,
    content: `Remind students the importance of using empathy in the classroom by printing out our empathy unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Empathy_Poster_UAE.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Empathy Poster (Arabic)',
    unit: 'Empathy',
    subtitle: 'Arabic Empathy Unit Poster',
    slug: Documents.posterEmpathyArabic,
    content: `Remind students the importance of using empathy in the classroom by printing out our Arabic empathy poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/empathyArabic.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Empathy Spanish',
    unit: 'Empathy',
    subtitle: 'Spanish Empathy Unit Poster',
    slug: Documents.posterEmpathySpanish,
    content: `Remind students the importance of using empathy in the classroom by printing out our empathy unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/empathyES.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Teamwork Poster',
    unit: 'Teamwork',
    subtitle: 'Teamwork Unit Poster',
    slug: Documents.posterTeamwork,
    content: `Build students' teamwork skills in class by printing out our teamwork unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Teamwork_Poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'Teamwork Poster (Arabic)',
    unit: 'Teamwork',
    subtitle: 'Arabic Teamwork Unit Poster',
    slug: Documents.posterTeamworkArabic,
    content: `Build students' teamwork skills in class by printing out our Arabic teamwork poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/teamworkArabic.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Teamwork Spanish',
    unit: 'Teamwork',
    subtitle: 'Spanish Teamwork Unit Poster',
    slug: Documents.posterTeamworkSpanish,
    content: `Build students' teamwork skills in class by printing out our teamwork unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/teamworkES.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Kindness Poster',
    unit: 'Kindness',
    subtitle: 'Kindness Unit Poster',
    slug: Documents.posterKindness,
    content: `Encourage kindness within your classroom by printing out our kindness unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Kindness_Poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Kindness Poster',
    unit: 'Kindness',
    subtitle: 'Kindness Unit Poster',
    slug: Documents.posterKindnessUAE,
    content: `Encourage kindness within your classroom by printing out our kindness unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Kindness_Poster_UAE.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Kindness Poster (Arabic)',
    unit: 'Kindness',
    subtitle: 'Arabic Kindness Unit Poster',
    slug: Documents.posterKindnessArabic,
    content: `Encourage kindness within your classroom by printing out our Arabic kindness poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/kindnessArabic.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Kindness Spanish',
    unit: 'Kindness',
    subtitle: 'Spanish Kindness Unit Poster',
    slug: Documents.posterKindnessSpanish,
    content: `Encourage kindness within your classroom by printing out our kindness unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/kindnessES.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Honesty Poster',
    unit: 'Honesty',
    subtitle: 'Honesty Unit Poster',
    slug: Documents.posterHonesty,
    content: `Help students be truthful with their words and gain trust by printing out our honesty unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Honesty_Poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Honesty Poster',
    unit: 'Honesty',
    subtitle: 'Honesty Unit Poster',
    slug: Documents.posterHonestyUAE,
    content: `Help students be truthful with their words and gain trust by printing out our honesty unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Honesty_Poster_UAE.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Honesty Poster (Arabic)',
    unit: 'Honesty',
    subtitle: 'Arabic Honesty Unit Poster',
    slug: Documents.posterHonestyArabic,
    content: `Help students be truthful with their words and gain trust by printing out our Arabic honesty poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/honestyArabic.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Honesty Spanish',
    unit: 'Honesty',
    subtitle: 'Spanish Honesty Unit Poster',
    slug: Documents.posterHonestySpanish,
    content: `Help students be truthful with their words and gain trust by printing out our honesty unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/honestyES.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Optimism Poster',
    unit: 'Optimism',
    subtitle: 'Optimism Unit Poster',
    slug: Documents.posterOptimism,
    content: `Increase positive thinking and optimism within your classroom by printing out our optimism unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Optimism_Poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Optimism Poster',
    unit: 'Optimism',
    subtitle: 'Optimism Unit Poster',
    slug: Documents.posterOptimismUAE,
    content: `Increase positive thinking and optimism within your classroom by printing out our optimism unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Optimism_Poster_UAE.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Optimism Poster (Arabic)',
    unit: 'Optimism',
    subtitle: 'Arabic Optimism Unit Poster',
    slug: Documents.posterOptimismArabic,
    content: `Increase positive thinking and optimism within your classroom by printing out our Arabic optimism poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/optimismArabic.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Optimism Spanish',
    unit: 'Optimism',
    subtitle: 'Spanish Optimism Unit Poster',
    slug: Documents.posterOptimismSpanish,
    content: `Increase positive thinking and optimism within your classroom by printing out our optimism unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/optimismES.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Courage Poster',
    unit: 'Courage',
    subtitle: 'Courage Unit Poster',
    slug: Documents.posterCourage,
    content: `Help students become courageous during class time by printing out our courage unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Courage_Poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Courage Poster',
    unit: 'Courage',
    subtitle: 'Courage Unit Poster',
    slug: Documents.posterCourageUAE,
    content: `Help students become courageous during class time by printing out our courage unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Courage_Poster_UAE.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Courage Poster (Arabic)',
    unit: 'Courage',
    subtitle: 'Arabic Courage Unit Poster',
    slug: Documents.posterCourageArabic,
    content: `Help students become courageous during class time by printing out our Arabic courage poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/courageArabic.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Courage Spanish',
    unit: 'Courage',
    subtitle: 'Spanish Courage Unit Poster',
    slug: Documents.posterCourageSpanish,
    content: `Help students become courageous during class time by printing out our courage unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/courageES.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Feelings Poster',
    subtitle: 'Extras',
    slug: Documents.bilingualFeelingsPoster,
    content:
      'Hang up our bilingual  Peekapak Feelings Poster in your classroom to guide students in identifying their emotions.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/BilingualFeelingsPoster.png`,
    filterTags: ['posters', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Peekapak Pals Activity & Coloring Book',
    subtitle: 'Extras',
    slug: Documents.peekapalsActivityBook,
    content: `Engage your students' creativity with this activity book! Includes connecting the dots, coloring, word scramble and puppets.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/colouring-book.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'Intro to the Peekapak Pals Cutouts',
    subtitle: 'Extras',
    slug: Documents.characterCardsMENA,
    content: `Help your students get to know the Peekapak Pals better by using these printable character cutouts in your classroom.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/intro-cutoutsUAE.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Intro to the Peekapak Pals Cutouts',
    subtitle: 'Extras',
    slug: Documents.characterCards,
    content: `Help your students get to know the Peekapak Pals better by using these printable character cutouts in your classroom.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/intro-cutouts.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Intro to the Peekapak Pals Cutouts Spanish',
    subtitle: 'Extras',
    slug: Documents.characterCardsES,
    content: `Help your students get to know the Peekapak Pals better by using these printable Spanish character cutouts in your classroom.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/es-intro-cutouts.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Classroom Streetscape',
    subtitle: 'Extras',
    slug: Documents.peekavilleStreetScape,
    content: `Use this printable classroom scene as a backdrop to create your own Peekapak story! Use the Peekapak Pals and Friends Cutouts to bring the scene to life!`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/streetscape.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'Peekapak Pals & Friends Cutouts',
    subtitle: 'Extras',
    slug: Documents.peekapalsCutouts,
    content: `Use these cutout characters to create your own story! Add them to your classroom streetscape, starring the Peekapak Pals and their friends!`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/friends-cutouts.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Peekapak Pals & Friends Cutouts',
    subtitle: 'Extras',
    slug: Documents.peekapalsCutoutsBOTH,
    content: `Use these cutout characters to create your own story! Add them to your classroom streetscape, starring the Peekapak Pals and their friends. Available in English and Spanish.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/friends-cutouts.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Meet the Peekapak Pals Sign',
    subtitle: 'Extras',
    slug: Documents.peekapakPalsSign,
    content: `Introduce your students to the world of Peekapak! Print and display this sign to create a welcoming space where kids can meet our lovable characters and explore social-emotional learning through engaging stories and activities.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/peekapakPalsSign.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'Regulation Station Sign',
    subtitle: 'Extras',
    slug: Documents.regulationStationSign,
    content: `Create a dedicated space in your classroom to help students recognize and manage their emotions. Print and display this sign to set up your own Regulation Station, where students can pause, reflect, and use self-regulation strategies to get back on track.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/regulationStationSign.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'You Can Do It Colouring Page',
    subtitle: 'Extras',
    slug: Documents.youCanDoItColoring,
    content: `We could all use a little help from our friends. Color in this picture and give it to a friend to give them a little encouragement!`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/you-can-do-it.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'Peekapak Valentine’s Day Cards',
    subtitle: 'Extras',
    slug: Documents.valentinesCards,
    content: `Celebrate Valentine’s Day with the Peekapak Pals! Print out these Valentine’s Day cards to share with your classroom this February 14th.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/valentines.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'Peekapak Valentine’s Day Cards (Spanish)',
    subtitle: 'Extras',
    slug: Documents.valentinesCardsES,
    content: `Celebrate Valentine’s Day with the Peekapak Pals! Print out these Valentine’s Day cards in Spanish to share with your classroom this February 14th.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/valentinesES.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Peekapak Certificates of Achievement',
    subtitle: 'Extras',
    slug: Documents.peekapalsCertificates,
    content: `Recognize your students' SEL learnings by recognizing them with our SEL certificates of achievement!`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/certificate.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
  },
  {
    displayName: 'Peekapak Certificates of Achievement (Spanish)',
    subtitle: 'Extras',
    slug: Documents.peekapalsCertificatesES,
    content: `Recognize your students' SEL learnings by recognizing them with our Spanish SEL certificates of achievement!`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/certificateES.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Intro Letter from Peekapak Pals',
    subtitle: 'Extras',
    slug: Documents.introLetter,
    content: `Hang up this welcome letter from the Peekapak Pals in your classroom to help your students get to know the characters in our stories!`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/intro-letter.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Intro Letter from Peekapak Pals',
    subtitle: 'Extras',
    slug: Documents.introLetterBOTH,
    content: `Hang up this welcome letter from the Peekapak Pals in your classroom to help your students get to know the characters in our stories! Available in English and Spanish.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/intro-letter.png`,
    filterTags: ['extras', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Class Norms ',
    subtitle: 'Posters',
    slug: Documents.posterClassNorms,
    content: `Print and showcase our Class Norms poster to promote positive student behavior and active participation in your classroom.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/class-norms-poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [9, 10, 11, 12],
    type: 'link',
  },
  {
    displayName: 'Class Norms (Arabic)',
    subtitle: 'Arabic Posters',
    slug: Documents.posterClassNormsArabicHSMS,
    content: `Reinforce safe space norms that will be used throughout the year as students share personal stories with our Class Norms poster.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/ClassNormsArabicHSMS.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [9, 10, 11, 12],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Self Awareness',
    subtitle: 'Posters',
    slug: Documents.posterIdentitySelfAwareness,
    content: `Reinforce self awareness concepts taught in class by printing out our Self Awareness unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/identity-self-awareness-unit-poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [6, 7, 8],
    type: 'link',
    unit: 'identity-standard',
  },
  {
    displayName: 'Self Awareness',
    subtitle: 'Posters',
    slug: Documents.posterIdentitySelfAwarenessHS,
    content: `Reinforce self awareness concepts taught in class by printing out our Self Awareness unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/self-awareness-HS-poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [9, 10, 11, 12],
    type: 'link',
    unit: 'hsidentity-standard',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Self Awareness',
    subtitle: 'Posters',
    slug: Documents.posterSelfAwarenessHSUAE,
    content: `Reinforce self awareness concepts taught in class by printing out our Self Awareness unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/MENA+Self-Awareness+Unit+Poster+(Gr9-12).png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [9, 10, 11, 12],
    type: 'link',
    unit: 'hsidentity-standard',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Self Awareness (Arabic)',
    subtitle: 'Arabic Posters',
    slug: Documents.posterSelfAwarenessArabicHSMS,
    content: `Reinforce self awareness concepts taught in class by printing out our Arabic Self Awareness unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/SelfAwarenessArabicHSMS.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Stress & Regulation',
    subtitle: 'Posters',
    slug: Documents.posterStressRegulation,
    content: `Encourage emotional regulation strategies in the classroom by printing out our Stress and Regulation unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/stress-regulation-unit-poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [6, 7, 8],
    type: 'link',
    unit: 'stress',
  },
  {
    displayName: 'Stress & Regulation',
    subtitle: 'Posters',
    slug: Documents.posterStressRegulationHS,
    content: `Encourage emotional regulation strategies in the classroom by printing out our Stress and Regulation unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/stress-regulation-HS-poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [9, 10, 11, 12],
    type: 'link',
    unit: 'hsstress',
  },
  {
    displayName: 'Stress & Regulation (Arabic)',
    subtitle: 'Arabic Posters',
    slug: Documents.posterStressAndRegulationArabicHSMS,
    content: `Reinforce stress and regulation concepts taught in class by printing out our Arabic Stress & Regulation unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/StressAndRegulationArabicHSMS.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Decision Making Skills',
    subtitle: 'Posters',
    slug: Documents.posterDecisionMakingSkills,
    content: `Encourage the use of decision making skills and strategies taught in class by printing out our Decision Making Skills unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/decision-making-skills-unit-poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [6, 7, 8],
    type: 'link',
    unit: 'decision',
  },
  {
    displayName: 'Decision Making Skills',
    subtitle: 'Posters',
    slug: Documents.posterDecisionMakingSkillsHS,
    content: `Encourage the use of decision making skills and strategies taught in class by printing out our Decision Making Skills unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/decision-making-HS-poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [9, 10, 11, 12],
    type: 'link',
    unit: 'hsdecision',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Decision Making Skills',
    subtitle: 'Posters',
    slug: Documents.posterDecisionMakingSkillsHSUAE,
    content: `Encourage the use of decision making skills and strategies taught in class by printing out our Decision Making Skills unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/MENA+Decision+Making+Skills+Unit+Poster+(Gr9-12).png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [9, 10, 11, 12],
    type: 'link',
    unit: 'hsdecision',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Decision Making Skills (Arabic)',
    subtitle: 'Arabic Posters',
    slug: Documents.posterDecisionArabicHSMS,
    content: `Reinforce decision making concepts taught in class by printing out our Arabic Decision Making Skillss unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/DecisionArabicHSMS.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Belonging & Peer Influence',
    subtitle: 'Posters',
    slug: Documents.posterBelongingPeerInfluence,
    content: `Help students understand the importance of community and mutual respect by printing out our Belonging and Peer Influence unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/belonging-peer-influence-unit-poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [6, 7, 8],
    type: 'link',
    unit: 'belonging',
  },
  {
    displayName: 'Belonging & Peer Influence',
    subtitle: 'Posters',
    slug: Documents.posterBelongingPeerInfluenceHS,
    content: `Help students understand the importance of community and mutual respect by printing out our Belonging and Peer Influence unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/belonging-peer-influence-HS-poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [9, 10, 11, 12],
    type: 'link',
    unit: 'hsbelonging',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Belonging & Peer Influence',
    subtitle: 'Posters',
    slug: Documents.posterBelongingHSUAE,
    content: `Help students understand the importance of community and mutual respect by printing out our Belonging and Peer Influence unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/MENA+Belonging+%26+Peer+Influence+Unit+Poster+(Gr9-12).png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [9, 10, 11, 12],
    type: 'link',
    unit: 'hsbelonging',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Belonging & Peer Influence (Arabic)',
    subtitle: 'Arabic Posters',
    slug: Documents.posterBelongingArabicHSMS,
    content: `Reinforce belonging and peer influence concepts taught in class by printing out our Arabic Belonging & Peer Influence unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/BelongingArabicHSMS.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Empathy & Conflict Resolution',
    subtitle: 'Posters',
    slug: Documents.posterEmpathyConflictResolution,
    content: `Give students the tools to approach conflicts with empathy and use perspective taking strategies by printing out our Empathy and Conflict Resolution unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/empathy-conflict-resolution-unit-poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [6, 7, 8],
    type: 'link',
    unit: 'empathy',
  },
  {
    displayName: 'Empathy & Conflict Resolution',
    subtitle: 'Posters',
    slug: Documents.posterEmpathyConflictResolutionHS,
    content: `Give students the tools to approach conflicts with empathy and use perspective taking strategies by printing out our Empathy and Conflict Resolution unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/empathy-conflict-resolution-HS-poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [9, 10, 11, 12],
    type: 'link',
    unit: 'hsempathy',
  },
  {
    displayName: 'Empathy & Conflict Resolution (Arabic)',
    subtitle: 'Arabic Posters',
    slug: Documents.posterEmpathyArabicHSMS,
    content: `Reinforce empathy and conflict resolution concepts taught in class by printing out our Arabic Empathy & Conflict Resolution unit poster and hanging it up in class.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/EmpathyArabicHSMS.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Spanish Posters',
    subtitle: 'Spanish Unit Posters',
    slug: Documents.postersSpanishMS,
    content: `Reinforce unit skills taught in class in Spanish, by printing out our unit posters and hanging it up in class. `,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/2023+-+Unit+0%2C+ES.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [6, 7, 8],
    applicationTags: ['standard', 'not-mena'],
    type: 'link',
    unit: 'identity-standard',
  },
  {
    displayName: 'Spanish Posters',
    subtitle: 'Spanish Unit Posters',
    slug: Documents.postersSpanishHS,
    content: `Reinforce unit skills taught in class in Spanish, by printing out our unit posters and hanging it up in class. `,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/ES_HS_Poster.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [9, 10, 11, 12],
    applicationTags: ['standard', 'not-mena'],
    type: 'link',
    unit: 'identity-standard',
  },
  {
    displayName: 'Peekapak Certificates of Achievement',
    subtitle: 'Extras',
    slug: Documents.peekapalsCertificatesMS,
    content: `Recognize your students SEL achievements by providing them with our personalized SEL certificates of achievement!`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Certificates-of-Achievement-Secondary-School.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
  },
  {
    displayName: 'PK-5 Journal Templates',
    subtitle: 'Extras',
    slug: Documents.pkto5JournalTemplates,
    content:
      'Help students express their feelings with these fun print outs, including journaling prompts, feelings check-ins and emotional vocabulary building.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/PK-5-Journal-Templates.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    buttonText: 'View',
    type: 'link',
  },
  {
    displayName: 'PK-5 Journal Templates (Spanish)',
    subtitle: 'Extras',
    slug: Documents.pkto5JournalTemplatesES,
    content:
      'Help students express their feelings in Spanish with these fun print outs, including journaling prompts, feelings check-ins and emotional vocabulary building.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/PK-5-Journal-TemplatesES.png`,
    filterTags: ['extras', 'freemium'],
    applicationTags: ['standard', 'not-mena'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    buttonText: 'View',
    type: 'link',
  },
  {
    displayName: 'Grade 6-12 Journal Templates',
    subtitle: 'Extras',
    slug: Documents.g6to12JournalTemplates,
    content:
      'Help students learn about their emotions with these print outs, including journaling prompts, feelings check-ins and emotional vocabulary building.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/6-12-Journal-Templates.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
  },
  {
    displayName: 'Grade 6-12 Journal Templates (Spanish)',
    subtitle: 'Extras',
    slug: Documents.g6to12JournalTemplatesES,
    content:
      'Help students learn about their emotions with these print outs in Spanish, including journaling prompts, feelings check-ins and emotional vocabulary building.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/6-12-Journal-TemplatesES.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Mindfulness Coloring',
    subtitle: 'Extras',
    slug: Documents.pkto5MindfulnessColoringPrintout,
    content: 'Encourage students to express their emotions through colors and patterns!',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/PK-5-Mindfullness-Coloring-Printout.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Mindfulness Coloring',
    subtitle: 'Extras',
    slug: Documents.pkto5MindfulnessColoringPrintoutBOTH,
    content:
      'Encourage students to express their emotions through colors and patterns! Available in English and Spanish.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/PK-5-Mindfullness-Coloring-Printout.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Mindfulness Doodling',
    subtitle: 'Extras',
    slug: Documents.g6to12MindfullnessDoodling,
    content: 'Encourage students to express their emotions and creativity through doodling!',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/G6-12-Mindfullness-Doodling.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Mindful Doodling',
    subtitle: 'Extras',
    slug: Documents.g6to12MindfullnessDoodlingBOTH,
    content:
      'Encourage students to express their emotions and creativity through doodling! Available in English and Spanish.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/G6-12-Mindfullness-Doodling.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'SMART Goal Template',
    subtitle: 'Extras',
    slug: Documents.SMARTGoalTemplate,
    content: 'Help students set and achieve their goals using the SMART goal steps.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Setting-SMART-Goals-with-Peekapak.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'SMART Goal Template',
    subtitle: 'Extras',
    slug: Documents.SMARTGoalTemplateBOTH,
    content: 'Help students set and achieve their goals using the SMART goal steps. Available in English and Spanish.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Setting-SMART-Goals-with-Peekapak.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Classroom Energizers',
    subtitle: 'Extras',
    slug: Documents.classroomEnergizersPkTo5,
    content:
      'Energizers are fun ways of re-focusing student attention, stimulating cognitive activity, and restoring momentum through short activities in the middle of a lesson or class.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Peekapak-Energizers-PK-5.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    buttonText: 'View',
    type: 'link',
  },
  {
    displayName: 'Classroom Energizers',
    subtitle: 'Extras',
    slug: Documents.classroomEnergizers6To12,
    content:
      'Energizers are fun ways of re-focusing student attention, stimulating cognitive activity, and restoring momentum through short activities in the middle of a lesson or class.',
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Peekapak-Energizers-6-12.png`,
    filterTags: ['extras', 'freemium'],
    gradeSpan: [6, 7, 8, 9, 10, 11, 12],
    buttonText: 'View',
    type: 'link',
  },
  {
    displayName: 'Peekapak Character Poster',
    subtitle: 'Extras',
    slug: Documents.posterCharacters,
    content: `These Peekapak characters are here to support big feelings, kind choices, and strong habits—every day. Download, print, and hang these Peekapak character posters to make wellbeing visible every day.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Character_Posters_english-letter.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
  {
    displayName: 'Peekapak Character Poster',
    subtitle: 'Extras',
    slug: Documents.posterCharactersUAE,
    content: `These Peekapak characters are here to support big feelings, kind choices, and strong habits—every day. Download, print, and hang these Peekapak character posters to make wellbeing visible every day.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Character_Posters_UAE-letter.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Peekapak Character (Arabic)',
    subtitle: 'Extras',
    slug: Documents.posterCharactersArabic,
    content: `These Peekapak characters are here to support big feelings, kind choices, and strong habits—every day. Download, print, and hang these Peekapak character posters to make wellbeing visible every day.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Character_Posters_arabic-letter.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['mena'],
  },
  {
    displayName: 'Peekapak Character Spanish',
    subtitle: 'Extra',
    slug: Documents.posterCharactersSpanish,
    content: `These Peekapak characters are here to support big feelings, kind choices, and strong habits—every day. Download, print, and hang these Peekapak character posters to make wellbeing visible every day.`,
    featuredImage: `${getPublicMediaRoot()}images/thumbnails/library/Character_Posters_spanish-letter.png`,
    filterTags: ['posters', 'freemium'],
    buttonText: 'View',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    type: 'link',
    applicationTags: ['standard', 'not-mena'],
  },
];

export const languageCard = [
  {
    displayName: 'n/a',
    content: 'n/a',
    type: 'language',
    filterTags: ['elementary'],
    applicationTags: ['standard', 'not-mena'],
    isTrialable: false,
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
  },
];

export const myPeekavillePlaceholderCard: AllCardData[] = [
  {
    displayName: 'myPeekaville Game Access',
    subtitle: 'myPeekaville Activity',
    content: '',
    filterTags: ['elementary', 'freemium'],
    label: 'myPeekaville',
    type: 'placeholder',
    gradeSpan: [-1, 0, 1, 2, 3, 4, 5],
    buttonText: 'Upgrade to play',
    unit: '',
  },
];

export const getElementaryFreemiumUnits = () => {
  const userEnableTags = getUsersEnableContentTags() || [];
  const userDisableTags = getUsersDisableContentTags() || [];

  return filterCardsByContentTags(elementaryFreemiumLessons, userEnableTags, userDisableTags);
};

export const getElementarySchoolUnits = () => {
  const userEnableTags = getUsersEnableContentTags() || [];
  const userDisableTags = getUsersDisableContentTags() || [];

  return filterCardsByContentTags(elementaryUnits, userEnableTags, userDisableTags);
};

// filter out extension units that user is not enabled for
export const getMiddleSchoolUnits = () => {
  const userEnableTags = getUsersEnableContentTags() || [];
  const userDisableTags = getUsersDisableContentTags() || [];

  return filterCardsByContentTags(middleSchoolUnits, userEnableTags, userDisableTags);
};

// filter out extension units that user is not enabled for
export const getHighSchoolUnits = () => {
  const userEnableTags = getUsersEnableContentTags() || [];
  const userDisableTags = getUsersDisableContentTags() || [];

  return filterCardsByContentTags(highSchoolUnits, userEnableTags, userDisableTags);
};

// Takween-specific getter functions - returns units without filtering
// (Takween users get ALL Takween content regardless of tags)
export const getTakweenUnits = () => {
  return takweenUnits;
};

// filter out extension units that user is not enabled for
export const getLinkCards = () => {
  const userEnableTags = getUsersEnableContentTags() || [];
  const userDisableTags = getUsersDisableContentTags() || [];

  return filterCardsByContentTags(linkCards, userEnableTags, userDisableTags);
};

// filter out extension units that user is not enabled for
export const getVideoCards = () => {
  const userEnableTags = getUsersEnableContentTags() || [];
  const userDisableTags = getUsersDisableContentTags() || [];

  return filterCardsByContentTags(videoLessons, userEnableTags, userDisableTags);
};

// Returns true if there are any video cards available for the current user
// that match the given classroom grade. Accounts for both applicationTag
// filtering (e.g. MENA users) and gradeSpan filtering (e.g. grades 6-12
// have no video content). Pass the classroom's numeric grade value.
export const hasVideoCards = (classroomGrade?: number): boolean => {
  const cards = getVideoCards();
  if (cards.length === 0) return false;
  if (classroomGrade === undefined) return true;
  return cards.some((card) => !card.gradeSpan || card.gradeSpan.includes(classroomGrade));
};

export function filterCardsByContentTags(
  cards: ElementaryCard[] | MiddleSchoolCard[] | LinkCard[] | VideoCard[] | ElementaryLessonCard[],
  userEnableTags: string[],
  userDisabledTags: string[],
): ElementaryCard[] | MiddleSchoolCard[] | LinkCard[] {
  const enabled: ElementaryCard[] | MiddleSchoolCard[] | LinkCard[] = cards.filter((card) => {
    // if there are no tags, it should be included always
    if (!card.applicationTags) return true;
    return card.applicationTags.every((tag) => userEnableTags.includes(tag)) || isStandardContent(card);
  });

  const final: ElementaryCard[] | MiddleSchoolCard[] | LinkCard[] = enabled.filter((card) => {
    if (!card.applicationTags) return true;
    const anyExcludeTagsMatched = card.applicationTags.some((tag) => userDisabledTags.includes(tag));
    return anyExcludeTagsMatched ? false : true;
  });

  return final;

  function isStandardContent(card: LibraryCardData) {
    if (!card.applicationTags) return false;
    return card.applicationTags.includes('standard');
  }
}
